import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  Alert,
  CircularProgress,
  Chip,
  InputAdornment,
  Tooltip,
  DialogTitle,
  Checkbox,
  Radio,
  FormControlLabel,
  Pagination
} from '@mui/material';
import { 
  Search as SearchIcon,
  Visibility,
  VisibilityOff,
  Replay as ReplayIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  LocalShipping as LocalShippingIcon,
  Inventory as InventoryIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  Store as StoreIcon,
  Home as HomeIcon,
  ContentCopy as ContentCopyIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Visibility as VisibilityIcon,
  CallMerge as CallMergeIcon,
  CallSplit as CallSplitIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useSnackbar } from 'notistack';
import SFInternationalShippingModal, { SFShippingResult } from './SFInternationalShippingModal';
import { isIntlOrder, intlCountryOf, intlRegionOf, isSfIntlOrder, sfCountryOf } from '../config/intlRegions';

interface Sale {
  id?: string;
  order_id?: string;
  outlet_item_id?: string;
  source?: string;
  image_url?: string;
  all_images?: string | any[]; 
  title?: string;
  display_sku?: string;
  sku?: string;
  variant_details?: string;
  slot_number?: number | null;   // outlet shelf-bin (貨架格) where the physical item sits
  customer_name?: string;
  customer_phone?: string;
  customer_email?: string;
  original_price?: string;
  sale_price?: string;                 // GROSS per-line price (VIP-adjusted, BEFORE 購物金)
  credit_applied?: string;             // redeemed 購物金 (store credit) share attributed to this outlet line
  net_sale_price?: string;             // sale_price − credit_applied, precomputed by /api/outlet/admin/sales
  order_store_credit_used?: string;    // orders.store_credit_used for this order (whole-order 購物金)
  order_total_amount?: string;         // orders.total_amount — authoritative amount owed; caps the 代收 figure
  delivery_method?: string;
  logistics_sub_type?: string;
  store_id?: string;
  store_name?: string;
  tracking_number?: string;
  is_hidden?: boolean;
  is_received?: boolean;
  is_sent?: boolean;
  ecpay_logistics_id?: string;
  payment_no?: string;
  cvs_validation_no?: string;
  payment_method?: string;
  // ECPay payment status fields (only for credit card payments):
  payment_status?: string;      // SUCCESS, PENDING, FAILED, etc.
  payment_trade_no?: string;    // ECPay trade number
  payment_date?: string;        // Payment completion date
  // LINE Pay transaction fields (only for line_pay payments):
  linepay_status?: string;            // REQUESTED | CONFIRMED | PARTIALLY_REFUNDED | REFUNDED | FAILED
  linepay_amount?: number;            // Original LINE Pay amount
  linepay_refunded_amount?: number;   // Cumulative refunded amount
  linepay_transaction_id?: string;    // LINE Pay transaction id
  shipping_address?: string;    // Home delivery address
  hsinchu_tracking_number?: string;  // Hsinchu logistics tracking
  hsinchu_carrier?: string;          // Hsinchu carrier name
  merged_into?: string | null;  // If set, this order is folded into a primary order's combined shipment
  quantity?: number | string;
  // International (SF Express) — present only for intl orders (HK/MO …)
  shipping_country?: string;        // intl country code, e.g. 'HK' | 'MO'
  order_customer_phone?: string;    // orders.customer_phone (intl mobile), distinct from outlet row phone
  sf_waybill_no?: string | null;
  sf_label_url?: string | null;
  sf_invoice_url?: string | null;
  sf_customer_order_no?: string | null;
  sf_cancelled_at?: string | null;
  sf_last_error?: string | null;
}

// Net cash for a sales line = gross sale_price minus the redeemed 購物金 (store credit)
// share attributed to it. The admin API (/api/outlet/admin/sales) returns net_sale_price
// precomputed; fall back to sale_price − credit_applied, then to sale_price for rows with
// no credit data (e.g. Shopee lines, which never use 購物金). This is the amount the
// customer still owes — what the 出貨/代收 (COD) figure must be.
function lineNetAmount(item: Sale): number {
  const raw = item.net_sale_price;
  if (raw !== undefined && raw !== null && `${raw}` !== '') {
    const n = parseFloat(`${raw}`);
    if (!isNaN(n)) return Math.max(0, Math.floor(n));
  }
  const gross = parseFloat(item.sale_price || '0') || 0;
  const credit = parseFloat(`${item.credit_applied ?? '0'}`) || 0;
  return Math.max(0, Math.floor(gross - credit));
}

function sumNetAmount(items: Sale[]): number {
  return items.reduce((sum, it) => sum + lineNetAmount(it), 0);
}

// 貨到付款 (COD) split for a shipment group.
//
// A combined shipment (合併出貨) can mix payment methods — e.g. a 貨到付款 order shipped in
// the same parcel as one the customer already paid by card / LINE Pay. Only the store_cod
// lines are still owed; charging the group total at the counter collects the paid order a
// SECOND time (ORD-16379 + ORD-16417 collected 1284 instead of 480 on 2026-08-12).
// `cod` is what ECPay may collect; `paid` is what must never be added to it.
//
// Deliberately line-based (lineNetAmount), so an all-COD parcel keeps the exact amount it
// has always been charged — this function only removes the already-paid members. If the
// `order_total_amount` cap lands, apply it PER ORDER inside this split, not to the group
// total, or the mixed-payment double-charge comes straight back.
function splitCodAmount(items: Sale[]): { cod: number; paid: number; total: number } {
  let cod = 0;
  let paid = 0;
  items.forEach((it) => {
    const net = lineNetAmount(it);
    if (it.payment_method === 'store_cod') cod += net;
    else paid += net;
  });
  return { cod, paid, total: cod + paid };
}

// Authoritative ceiling for the 代收 (COD) figure.
//
// Every amount above is derived from outlet_sales, which is NOT what the customer owes.
// It misses order-level discounts (VIP / 生日 / promo live in orders.discount_amount and
// never reach sale_price), it misses 購物金 recorded on the order but never attributed
// down to credit_applied, and it is simply wrong when a stray outlet_sales row is attached
// to the order. Each of those overcharges the customer at the counter — confirmed against
// ECPay's CollectionAmount on 13 shipped orders totalling NT$4,160, e.g. ORD-16368
// (2026-08-07) collected 1002 on a 900 order after a 102 生日折扣.
//
// orders.total_amount is the number the customer agreed to at checkout, so it is the cap.
// Only 貨到付款 orders count: an order in the same parcel that the customer already paid
// for (card / LINE Pay) must never raise the ceiling. Returns null (= don't cap) if any
// COD order in the group has no usable total, so a missing field can never under-collect.
function codTotalCap(items: Sale[]): number | null {
  const totalsByOrder = new Map<string, number>();
  for (const it of items) {
    if (it.payment_method !== 'store_cod' || !it.order_id) continue;
    const raw = it.order_total_amount;
    if (raw === undefined || raw === null || `${raw}` === '') return null;
    const n = parseFloat(`${raw}`);
    if (isNaN(n)) return null;
    totalsByOrder.set(it.order_id, Math.max(0, Math.floor(n)));
  }
  if (totalsByOrder.size === 0) return null;
  return Array.from(totalsByOrder.values()).reduce((sum, v) => sum + v, 0);
}

// Gross (pre-credit) sum — used only to show the 購物金 deduction breakdown in the 出貨 dialog.
function sumGrossAmount(items: Sale[]): number {
  return items.reduce((sum, it) => sum + Math.floor(parseFloat(it.sale_price || '0')), 0);
}

// International (HK/MO …) orders ship via SF Express International, not TW pickup/宅配.
// Delegates to the shared registry helper (config/intlRegions.ts); a Sale carries
// delivery_method + shipping_country at the top level, which the helper reads.
function isIntlSale(sale?: Sale): boolean {
  return isIntlOrder(sale);
}

interface ProductMetadata {
  product_key: string;
  tag: string;
  reference_image?: string;
  referenceImage?: string;
  createdAt: string;
  lastUpdated: string;
}

interface Props {
  salesHistory?: Sale[];
  loadingSales?: boolean;
  hiddenOrders?: string[];
  showHidden?: boolean;
  setSalesHistorySearch?: (value: string) => void;
  salesHistorySearch?: string;
  fetchSalesHistory?: () => void;
  showMessage: (message: string, variant: 'success' | 'error' | 'info' | 'warning') => void;
  setShowHidden?: (value: boolean) => void;
  filteredSalesHistory?: Sale[];
  // ADD THESE NEW PROPS:
  updateSaleInList?: (orderId: string, updates: Partial<Sale>) => void;
  addToHiddenOrders?: (orderId: string) => void;
  removeFromHiddenOrders?: (orderId: string) => void;
  productMetadata?: {[key: string]: ProductMetadata};
}

// Custom date formatter
function formatDate(dateString?: string): string {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString();
}

function calculateSavingsPercent(originalPrice?: string, discountPrice?: string): number {
  const original = parseFloat(originalPrice || '0');
  const discount = parseFloat(discountPrice || '0');
  if (isNaN(original) || original === 0) return 0;
  return Math.round(((original - discount) / original) * 100);
}

// Convert hex string to base64 for shipping label display
function hexToBase64(hexString: string): string {
  const bytes = [];
  for (let i = 0; i < hexString.length; i += 2) {
    bytes.push(parseInt(hexString.substr(i, 2), 16));
  }
  const uint8Array = new Uint8Array(bytes);
  let binaryString = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binaryString += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binaryString);
}

// Open shipping label in a new tab for printing
function displayShippingLabel(hexImage: string, labelData: { trackingNumber?: string; recipientName?: string; recipientAddress?: string; weight?: number } = {}) {
  if (!hexImage) return;
  const base64Image = hexToBase64(hexImage);
  const newWindow = window.open('', '_blank');
  if (!newWindow) {
    alert('請允許彈出視窗以檢視出貨標籤');
    return;
  }
  const htmlContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>宅配出貨單 - ${labelData.trackingNumber || ''}</title>
<style>body{margin:0;padding:20px;font-family:Arial;background:#f5f5f5;display:flex;flex-direction:column;align-items:center}
.header{text-align:center;margin-bottom:20px;background:white;padding:15px;border-radius:8px;box-shadow:0 2px 4px rgba(0,0,0,.1);width:100%;max-width:800px}
.header h1{color:#2c3e50;margin:0 0 10px}
.tracking-info{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;margin-top:15px;text-align:left}
.info-item{background:#f8f9fa;padding:8px 12px;border-radius:4px;border-left:3px solid #3498db}
.info-label{font-weight:bold;color:#2c3e50;font-size:.9em}.info-value{color:#34495e;margin-top:2px}
.label-container{background:white;padding:20px;border-radius:8px;box-shadow:0 2px 4px rgba(0,0,0,.1);max-width:100%;overflow:auto}
.shipping-label{max-width:100%;height:auto;border:1px solid #ddd;border-radius:4px}
.actions{margin-top:20px;text-align:center}
.btn{background:#3498db;color:white;border:none;padding:10px 20px;border-radius:4px;cursor:pointer;font-size:14px;margin:0 5px}
.btn:hover{background:#2980b9}.btn-print{background:#27ae60}.btn-print:hover{background:#229954}
@media print{body{background:white;padding:0}.header,.actions{display:none}.label-container{box-shadow:none;padding:0;margin:0}}
</style></head><body>
<div class="header"><h1>宅配出貨單</h1><p>新竹物流</p>
${labelData.trackingNumber ? `<div class="tracking-info">
<div class="info-item"><div class="info-label">追蹤號碼</div><div class="info-value">${labelData.trackingNumber}</div></div>
${labelData.recipientName ? `<div class="info-item"><div class="info-label">收件人</div><div class="info-value">${labelData.recipientName}</div></div>` : ''}
${labelData.recipientAddress ? `<div class="info-item"><div class="info-label">收件地址</div><div class="info-value">${labelData.recipientAddress}</div></div>` : ''}
</div>` : ''}</div>
<div class="label-container"><img src="data:image/png;base64,${base64Image}" alt="Shipping Label" class="shipping-label"></div>
<div class="actions"><button class="btn btn-print" onclick="window.print()">列印</button><button class="btn" onclick="window.close()">關閉</button></div>
</body></html>`;
  newWindow.document.write(htmlContent);
  newWindow.document.close();
}

// Helper function to determine document type based on store ID and logistics type
function determineDocumentType(storeId?: string, logisticsSubType?: string): string {
  if (logisticsSubType) return logisticsSubType;
  if (storeId) {
    if (storeId.startsWith('F') || /^[A-Z]\d{5}$/.test(storeId)) return 'FAMIC2C';
  }
  return 'UNIMARTC2C';
}


// Function to get delivery method display information
function getDeliveryMethodInfo(method: string, storeData: { logisticsSubType: any; storeId?: any; }) {
  if (method === 'pickup') {
    const storeType = storeData && storeData.logisticsSubType ? storeData.logisticsSubType : '';
    let label = '超商取貨';
    let icon = <StoreIcon fontSize="small" sx={{ mr: 1 }} />;
    
    if (storeType && typeof storeType === 'string' && storeType.includes('FAMIC2C')) {
      label = '超商(全家)';
    } else if (storeType && typeof storeType === 'string' && storeType.includes('UNIMARTC2C')) {
      label = '超商(7-11)';
    } else if (storeType && typeof storeType === 'string' && storeType.includes('HILIFEC2C')) {
      label = '超商(萊爾富)';
    } else if (storeType && typeof storeType === 'string' && storeType.includes('OKMARTC2C')) {
      label = '超商(OK mart)';
    }
    
    return { label, icon };
  } else if (method === 'shipping') {
    return { 
      label: '宅配', 
      icon: <HomeIcon fontSize="small" sx={{ mr: 1 }} />
    };
  }
  
  return {
    label: '未指定',
    icon: null
  };
}

// Normalize a TW phone so the same customer matches across orders
// (+886912345678 / 0912-345-678 → 0912345678).
function normalizePhone(phone?: string): string {
  if (!phone) return '';
  return String(phone).replace(/^\+886/, '0').replace(/[\s-]/g, '').trim();
}

// A stable key for a delivery destination. Two orders can ship as one parcel
// only when this matches (same 超商門市 for pickup, or same 宅配 address).
function destinationKey(s: Sale): string {
  if (s.delivery_method === 'shipping') {
    const a: any = s.shipping_address;
    const str = typeof a === 'string' ? a : a ? JSON.stringify(a) : '';
    return 'ship:' + str.replace(/\s/g, '');
  }
  return 'pickup:' + (s.store_id || '');
}

interface MergeGroup {
  groupId: string;       // primary order id (own id when not merged)
  orderIds: string[];    // all order ids in the group, primary first
  items: Sale[];         // all sale rows across the group
  primaryItem: Sale;     // representative row from the primary order
  isMerged: boolean;     // true when more than one order is combined
}

// Group sale rows into shipment groups: an order folded into a primary
// (merged_into) renders inside the primary's group instead of as its own card.
function buildMergeGroups(sales: Sale[]): MergeGroup[] {
  const byGroup: Record<string, Sale[]> = {};
  const orderOfKeys: string[] = [];
  sales.forEach((sale) => {
    const key = (sale.merged_into || sale.order_id || 'unknown') as string;
    if (!byGroup[key]) {
      byGroup[key] = [];
      orderOfKeys.push(key);
    }
    byGroup[key].push(sale);
  });
  return orderOfKeys.map((groupId) => {
    const items = byGroup[groupId];
    const orderIds = Array.from(new Set(items.map((i) => i.order_id).filter(Boolean))) as string[];
    // Primary first so the card header/actions use the order we ship under.
    orderIds.sort((a, b) => (a === groupId ? -1 : b === groupId ? 1 : 0));
    const primaryItem = items.find((i) => i.order_id === groupId) || items[0];
    return { groupId, orderIds, items, primaryItem, isMerged: orderIds.length > 1 };
  });
}

function SalesHistoryTab(props: Props) {
  const { 
    salesHistory = [], 
    loadingSales = false, 
    hiddenOrders = [], 
    showHidden = false,
    setSalesHistorySearch,
    salesHistorySearch = '',
    fetchSalesHistory,
    showMessage,
    setShowHidden,
    filteredSalesHistory = [],
    updateSaleInList,
    addToHiddenOrders,
    removeFromHiddenOrders,
    productMetadata = {} 
  } = props;
  
  const { enqueueSnackbar } = useSnackbar();
  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [currentOrder, setCurrentOrder] = useState<Sale | null>(null);
  const [processingShipping, setProcessingShipping] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [batchPrintDialogOpen, setBatchPrintDialogOpen] = useState(false);
  const [batchPrintLoading, setBatchPrintLoading] = useState(false);
  const [editableAmount, setEditableAmount] = useState<string>('');
  // Image gallery state
const [imageGalleryOpen, setImageGalleryOpen] = useState(false);
const [currentProductImages, setCurrentProductImages] = useState<string[]>([]);
const [currentImageIndex, setCurrentImageIndex] = useState(0);
const [selectedProductTitle, setSelectedProductTitle] = useState('');

// Notes editing state (key = "orderId::itemKey")
const [editingNotesKey, setEditingNotesKey] = useState<string | null>(null);
const [editingNotesValue, setEditingNotesValue] = useState('');
const [savingNotes, setSavingNotes] = useState(false);
const [orderItemNotes, setOrderItemNotes] = useState<Record<string, string>>({});

// Customer name editing state
const [editingNameOrderId, setEditingNameOrderId] = useState<string | null>(null);
const [editingNameValue, setEditingNameValue] = useState('');
const [savingName, setSavingName] = useState(false);

const startEditName = (orderId: string, currentName: string) => {
  setEditingNameOrderId(orderId);
  setEditingNameValue(currentName || '');
};

const cancelEditName = () => {
  setEditingNameOrderId(null);
  setEditingNameValue('');
};

const saveCustomerName = async (orderId: string, source: string | undefined) => {
  const trimmed = editingNameValue.trim();
  if (!orderId) return;
  setSavingName(true);
  try {
    // Shopee customer names come from Shopee API and aren't editable here.
    if (source === 'shopee') {
      enqueueSnackbar('蝦皮訂單的客戶姓名無法於此修改', { variant: 'warning' });
      setSavingName(false);
      return;
    }
    await api.patch(`/api/storefront/orders/${encodeURIComponent(orderId)}/customer`, {
      customerName: trimmed,
    });
    if (updateSaleInList) {
      updateSaleInList(orderId, { customer_name: trimmed });
    }
    enqueueSnackbar('客戶姓名已更新', { variant: 'success' });
    cancelEditName();
  } catch (err: any) {
    enqueueSnackbar(`更新客戶姓名失敗: ${err?.response?.data?.error || err.message}`, { variant: 'error' });
  } finally {
    setSavingName(false);
  }
};

// Refund state
const [refundDialogOpen, setRefundDialogOpen] = useState(false);
const [refundOrderId, setRefundOrderId] = useState('');
const [refundTradeNo, setRefundTradeNo] = useState('');
const [refundMaxAmount, setRefundMaxAmount] = useState(0);
const [refundAmount, setRefundAmount] = useState('');
const [refundFullAmount, setRefundFullAmount] = useState(true);
// 'credit_card' (ECPay) or 'line_pay' — determines which refund endpoint is used
const [refundPaymentMethod, setRefundPaymentMethod] = useState<'credit_card' | 'line_pay'>('credit_card');
const [processingRefund, setProcessingRefund] = useState(false);

// Combine-orders state. The combine feature is OUTLET-ONLY (Shopee controls its
// own logistics). Detection matches same customer by normalized phone; only
// same-destination orders are combinable into one shipment.
const [combineDialogOpen, setCombineDialogOpen] = useState(false);
const [combineCandidates, setCombineCandidates] = useState<Sale[]>([]);
const [combineSelectedIds, setCombineSelectedIds] = useState<string[]>([]);
const [combinePrimaryId, setCombinePrimaryId] = useState<string>('');
const [combineProcessing, setCombineProcessing] = useState(false);
// Order ids in the group currently being shipped (so secondaries get marked sent too)
const [shipGroupOrderIds, setShipGroupOrderIds] = useState<string[]>([]);

// HK 順豐 (SF International) shipping modal — holds the order-shaped object to ship
const [sfModalOrder, setSfModalOrder] = useState<any | null>(null);

// Pagination over shipment groups — keeps the "Show All Orders" view from
// dumping every hidden/completed order onto a single page.
const GROUPS_PER_PAGE = 20;
const [page, setPage] = useState(1);

// One representative sale row per OUTLET order id (first row wins).
const outletOrderReps = useMemo(() => {
  const reps: Record<string, Sale> = {};
  (salesHistory || []).forEach((s) => {
    if (s.source !== 'outlet' || !s.order_id) return;
    if (!reps[s.order_id]) reps[s.order_id] = s;
  });
  return reps;
}, [salesHistory]);

// Combined order total (sum of NET cash — post-購物金 — across all rows of an order id).
const orderTotals = useMemo(() => {
  const totals: Record<string, number> = {};
  (salesHistory || []).forEach((s) => {
    if (!s.order_id) return;
    totals[s.order_id] = (totals[s.order_id] || 0) + lineNetAmount(s);
  });
  return totals;
}, [salesHistory]);

// Orders already in a merge group (as primary OR secondary) — not re-combinable.
const mergedOrderIdSet = useMemo(() => {
  const set = new Set<string>();
  (salesHistory || []).forEach((s) => {
    if (s.merged_into && s.order_id) {
      set.add(s.order_id);
      set.add(s.merged_into);
    }
  });
  return set;
}, [salesHistory]);

// normalized phone -> list of representative rows for that customer's
// unshipped, not-yet-combined outlet orders.
const customerUnshippedOutlet = useMemo(() => {
  const map: Record<string, Sale[]> = {};
  Object.values(outletOrderReps).forEach((s) => {
    if (s.is_sent) return;
    if (s.order_id && mergedOrderIdSet.has(s.order_id)) return;
    const phone = normalizePhone(s.customer_phone);
    if (!phone) return;
    (map[phone] = map[phone] || []).push(s);
  });
  return map;
}, [outletOrderReps, mergedOrderIdSet]);

// For a given order, how many OTHER same-customer, same-destination outlet
// orders could be combined with it right now.
const combinableSiblingsFor = (order: Sale): Sale[] => {
  if (order.source !== 'outlet' || order.is_sent) return [];
  if (!order.delivery_method) return []; // no destination → can't ship as one parcel
  if (order.order_id && mergedOrderIdSet.has(order.order_id)) return [];
  const phone = normalizePhone(order.customer_phone);
  if (!phone) return [];
  const destK = destinationKey(order);
  return (customerUnshippedOutlet[phone] || []).filter(
    (s) => s.order_id !== order.order_id && !!s.delivery_method && destinationKey(s) === destK
  );
};

// Reset to the first page whenever the result set changes (search, refresh,
// or the Show-All toggle) so we never land on an out-of-range page.
useEffect(() => {
  setPage(1);
}, [salesHistorySearch, showHidden, filteredSalesHistory.length]);

// Fetch order item notes when sales data changes
useEffect(() => {
  const orderIds = Array.from(new Set(salesHistory.map(s => s.order_id).filter(Boolean))) as string[];
  if (orderIds.length === 0) return;

  api.post('/api/products/order-item-notes/batch', { orderIds })
    .then(res => setOrderItemNotes(res.data || {}))
    .catch(err => console.error('Error fetching order item notes:', err));
}, [salesHistory]);

  function getPaymentMethodInfo(paymentMethod?: string, paymentStatus?: string) {
  if (paymentMethod === 'credit_card') {
    // For credit card, show status-specific info
    switch (paymentStatus) {
      case 'SUCCESS':
        return {
          label: '信用卡 (已付款)',
          icon: '💳',
          color: 'success' as const,
          statusColor: 'success' as const
        };
      case 'PENDING':
        return {
          label: '信用卡 (處理中)',
          icon: '💳',
          color: 'warning' as const,
          statusColor: 'warning' as const
        };
      case 'FAILED':
        return {
          label: '信用卡 (失敗)',
          icon: '💳',
          color: 'error' as const,
          statusColor: 'error' as const
        };
      case 'REFUNDED':
        return {
          label: '信用卡 (已退刷)',
          icon: '💳',
          color: 'warning' as const,
          statusColor: 'warning' as const
        };
      default:
        return {
          label: '信用卡 (未知狀態)',
          icon: '💳',
          color: 'default' as const,
          statusColor: 'default' as const
        };
    }
  } else if (paymentMethod === 'line_pay') {
    return {
      label: 'LINE Pay (已付款)',
      icon: '💚',
      color: 'success' as const,
      statusColor: 'success' as const
    };
  } else if (paymentMethod === 'store_cod') {
    return {
      label: '貨到付款',
      icon: '💰',
      color: 'primary' as const,
      statusColor: 'primary' as const
    };
  } else {
    return {
      label: '未指定',
      icon: '❓',
      color: 'default' as const,
      statusColor: 'default' as const
    };
  }
}

  // Handle copy to clipboard
  function copyToClipboard(text: string | undefined) {
    if (!text) return;
    
    navigator.clipboard.writeText(text)
      .then(() => {
        enqueueSnackbar('已複製到剪貼簿', { variant: 'success' });
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        enqueueSnackbar('複製失敗', { variant: 'error' });
      });
  }

  // Handle opening image gallery
const handleOpenImageGallery = (item: Sale) => {
  const images: string[] = [];
  
  // Add main image
  if (item.image_url) {
    images.push(item.image_url);
  }
  
  // Try to parse all_images if it exists
  if (item.all_images) {
    try {
      const allImagesData = typeof item.all_images === 'string' 
        ? JSON.parse(item.all_images) 
        : item.all_images;
      
      if (Array.isArray(allImagesData)) {
        allImagesData.forEach((img: any) => {
          const imgSrc = typeof img === 'string' ? img : img.src;
          if (imgSrc && !images.includes(imgSrc)) {
            images.push(imgSrc);
          }
        });
      }
    } catch (error) {
      console.error('Error parsing all_images:', error);
    }
  }
  
  if (images.length > 0) {
    setCurrentProductImages(images);
    setCurrentImageIndex(0);
    setSelectedProductTitle(item.title || 'Product');
    setImageGalleryOpen(true);
  } else {
    enqueueSnackbar('沒有可用的產品圖片', { variant: 'info' });
  }
};

// Navigate to previous image
const handlePreviousImage = () => {
  setCurrentImageIndex(prev => 
    prev > 0 ? prev - 1 : currentProductImages.length - 1
  );
};

// Navigate to next image
const handleNextImage = () => {
  setCurrentImageIndex(prev => 
    prev < currentProductImages.length - 1 ? prev + 1 : 0
  );
};

// Handle keyboard navigation
const handleKeyPress = (event: React.KeyboardEvent) => {
  if (event.key === 'ArrowLeft') {
    handlePreviousImage();
  } else if (event.key === 'ArrowRight') {
    handleNextImage();
  } else if (event.key === 'Escape') {
    setImageGalleryOpen(false);
  }
};

  // Toggle hide/show order
  // groupOrderIds: every order in the 合併出貨 group (primary first). A combined group
  // renders as ONE card, so hiding it must hide every member — otherwise the folded-in
  // orders reappear as stray cards the moment the primary disappears. The backend
  // cascades over the group too; these ids only keep the optimistic UI in step.
  async function toggleHideOrder(
    orderId: string | undefined,
    source: string | undefined,
    isCurrentlyHidden: boolean,
    groupOrderIds?: string[]
  ) {
  if (!orderId) return;

  const affectedIds = Array.from(new Set([orderId, ...(groupOrderIds || [])])).filter(Boolean);

  // Optimistic update
  affectedIds.forEach((id) => {
    if (updateSaleInList) {
      updateSaleInList(id, { is_hidden: !isCurrentlyHidden });
    }
    if (isCurrentlyHidden) {
      removeFromHiddenOrders?.(id);
    } else {
      addToHiddenOrders?.(id);
    }
  });

  try {
    // Call the appropriate API based on current hidden status
    if (isCurrentlyHidden) {
      await api.post(`/api/outlet/admin/orders/${orderId}/show`);
      showMessage('Order is now visible', 'success');
    } else {
      await api.post(`/api/outlet/admin/orders/${orderId}/hide`, { source: source || 'unknown' });
      showMessage(
        affectedIds.length > 1
          ? `已隱藏合併出貨的 ${affectedIds.length} 筆訂單`
          : 'Order hidden successfully',
        'success'
      );
    }
  } catch (error) {
    console.error('Error toggling order visibility:', error);
    showMessage('Failed to update order visibility', 'error');

    // Revert optimistic update on error
    affectedIds.forEach((id) => {
      if (updateSaleInList) {
        updateSaleInList(id, { is_hidden: isCurrentlyHidden });
      }
      if (isCurrentlyHidden) {
        addToHiddenOrders?.(id);
      } else {
        removeFromHiddenOrders?.(id);
      }
    });
  }
}

  // Relist a dead order's unshipped outlet units: void the sale so the physical
  // garment returns to the sellable list (keeping its bin), then hide the order.
  // For no-shows / cancelled outlet orders where the garment is still on the shelf.
  async function relistOrderUnits(orderId: string | undefined, isSent: boolean | undefined) {
    if (!orderId) return;
    if (isSent) { showMessage('已出貨的訂單不可退回架上', 'warning'); return; }
    if (!window.confirm(
      `確定把訂單 ${orderId} 未出貨的出清商品退回架上？\n\n` +
      `會取消佔用、重新上架販售並保留貨架格，並隱藏這筆無效訂單。`
    )) return;
    try {
      const resp = await api.post(`/api/outlet/admin/orders/${orderId}/relist`, { hideOrder: true });
      showMessage(resp.data?.message || '已退回架上', 'success');
      addToHiddenOrders?.(orderId);
      updateSaleInList?.(orderId, { is_hidden: true });
      fetchSalesHistory?.();
    } catch (error: any) {
      console.error('Relist failed:', error);
      showMessage(error?.response?.data?.message || '退回架上失敗', 'error');
    }
  }

function groupSalesByOrder(sales: Sale[]): Record<string, Sale[]> {
  return sales.reduce((groups, sale) => {
    const orderId = sale.order_id || 'unknown';
    if (!groups[orderId]) {
      groups[orderId] = [];
    }
    groups[orderId].push(sale);
    return groups;
  }, {} as Record<string, Sale[]>);
}

  // Toggle received status
  async function toggleReceivedStatus(orderId: string | undefined, source: string | undefined, isCurrentlyReceived: boolean | undefined) {
  if (!orderId) return;
  
  const newReceivedStatus = !isCurrentlyReceived;
  
  // Optimistic update
  if (updateSaleInList) {
    updateSaleInList(orderId, { is_received: newReceivedStatus });
  }
  
  try {
    if (isCurrentlyReceived) {
      await api.post(`/api/outlet/admin/orders/${orderId}/unreceived`);
      showMessage('Order marked as not received', 'success');
    } else {
      await api.post(`/api/outlet/admin/orders/${orderId}/received`, { source: source || 'unknown' });
      showMessage('Order marked as received', 'success');
    }
  } catch (error) {
    console.error('Error updating received status:', error);
    showMessage('Failed to update received status', 'error');
    
    // Revert optimistic update on error
    if (updateSaleInList) {
      updateSaleInList(orderId, { is_received: isCurrentlyReceived });
    }
  }
}
  
  // Toggle sent status
  async function toggleSentStatus(orderId: string | undefined, source: string | undefined, isCurrentlySent: boolean | undefined) {
  if (!orderId) return;
  
  const newSentStatus = !isCurrentlySent;
  
  // Optimistic update
  if (updateSaleInList) {
    updateSaleInList(orderId, { is_sent: newSentStatus });
  }
  
  try {
    if (isCurrentlySent) {
      await api.post(`/api/outlet/admin/orders/${orderId}/unsent`);
      showMessage('Order marked as not sent', 'success');
    } else {
      await api.post(`/api/outlet/admin/orders/${orderId}/sent`, { source: source || 'unknown' });
      showMessage('Order marked as sent', 'success');
    }
  } catch (error) {
    console.error('Error updating sent status:', error);
    showMessage('Failed to update sent status', 'error');
    
    // Revert optimistic update on error
    if (updateSaleInList) {
      updateSaleInList(orderId, { is_sent: isCurrentlySent });
    }
  }
}

// Add this dialog component to your SalesHistoryTab
const BatchPrintDialog = () => {
  const selectedOrderDetails = selectedOrders.map(orderId => {
  const orderItems = Object.entries(groupSalesByOrder(filteredSalesHistory))
    .find(([id]) => id === orderId)?.[1];
  return orderItems ? orderItems[0] : null;
}).filter((order): order is Sale => order !== null);
  
  // Group by logistics type for display
  const ordersByType = selectedOrderDetails.reduce((acc, order) => {
    const logisticsType = order.logistics_sub_type || 'UNIMARTC2C';
    if (!acc[logisticsType]) {
      acc[logisticsType] = [];
    }
    acc[logisticsType].push(order);
    return acc;
  }, {} as Record<string, Sale[]>);
  
  return (
    <Dialog
      open={batchPrintDialogOpen}
      onClose={() => setBatchPrintDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        批次列印確認
        <IconButton
          onClick={() => setBatchPrintDialogOpen(false)}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent>
        <Alert severity="info" sx={{ mb: 3 }}>
          您即將批次列印 {selectedOrders.length} 張出貨單。不同超商類型將分別開啟新視窗。
        </Alert>
        
        {Object.entries(ordersByType).map(([logisticsType, orders]) => (
          <Box key={logisticsType} sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <PrintIcon sx={{ mr: 1 }} />
              {logisticsType === 'FAMIC2C' ? '全家超商' : '7-ELEVEN'} 
              <Chip label={`${orders.length} 張`} size="small" sx={{ ml: 1 }} />
            </Typography>
            
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>訂單編號</TableCell>
                    <TableCell>客戶</TableCell>
                    <TableCell>門市</TableCell>
                    <TableCell>物流編號</TableCell>
                    <TableCell>寄貨編號</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.order_id}>
                      <TableCell>#{order.order_id}</TableCell>
                      <TableCell>
                        {order.customer_name || order.customer_phone || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.store_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {order.store_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {order.ecpay_logistics_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {order.payment_no}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
        
        <Alert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>注意事項：</strong>
            <br />
            • 不同超商類型將分別開啟新的列印視窗
            <br />
            • 請確保您的瀏覽器允許彈出視窗
            <br />
            • 列印完成後，此對話框將自動關閉並清除選擇
          </Typography>
        </Alert>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={() => setBatchPrintDialogOpen(false)}
          disabled={batchPrintLoading}
        >
          取消
        </Button>
        <Button 
          onClick={confirmBatchPrint}
          variant="contained"
          disabled={batchPrintLoading}
          startIcon={batchPrintLoading ? <CircularProgress size={20} /> : <PrintIcon />}
        >
          {batchPrintLoading ? '處理中...' : `確認列印 ${selectedOrders.length} 張`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

  // Handle print shipping document
  async function handlePrintShippingDocument(order: Sale) {
  if (!order || !order.order_id) return;
  
  try {
    // Show loading message2
    enqueueSnackbar('正在準備出貨單...', { variant: 'info' });
    
    // For ECPay pickup orders, we need to print a different document
    if (order.delivery_method === 'pickup' && order.store_id) {
      // Use the orderId-based endpoint instead
      const response = await api.post(`/api/ecpay/logistics/print/${order.order_id}`, {
        platform: 'web',
        logistics_sub_type: order.logistics_sub_type
      });
      
      if (response && response.data) {
        // Create a blob from HTML content
        const blob = new Blob([response.data], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        
        // Open in new tab
        window.open(url, '_blank');
        
        // Clean up
        setTimeout(() => URL.revokeObjectURL(url), 100);
        
        enqueueSnackbar('出貨單已在新視窗中開啟', { variant: 'success' });
      } else {
        throw new Error('Invalid response format');
      }
    } else if (order.delivery_method === 'shipping' && order.sf_waybill_no) {
      // 順豐宅配 — open the saved SF label PDF
      if (order.sf_label_url) {
        const win = window.open(order.sf_label_url, '_blank', 'noopener,noreferrer');
        if (!win) enqueueSnackbar('請允許彈出視窗以開啟順豐面單', { variant: 'warning' });
      } else {
        enqueueSnackbar(`順豐單 ${order.sf_waybill_no} 沒有面單連結`, { variant: 'warning' });
      }
    } else if (order.delivery_method === 'shipping') {
      // Legacy 新竹物流 shipment — fetch and display the Hsinchu label
      const trackingNum = order.hsinchu_tracking_number || order.tracking_number;
      if (trackingNum) {
        const labelResponse = await api.get(`/api/hsinchu/label/${trackingNum}`);
        if (labelResponse.data?.success && labelResponse.data.data?.labelImage) {
          displayShippingLabel(labelResponse.data.data.labelImage, {
            trackingNumber: trackingNum,
            recipientName: order.customer_name || '',
            recipientAddress: ''
          });
          enqueueSnackbar('出貨標籤已在新視窗中開啟', { variant: 'success' });
        } else {
          enqueueSnackbar('無法取得出貨標籤', { variant: 'warning' });
        }
      } else {
        enqueueSnackbar('此宅配訂單尚未建立出貨單，請先點擊「宅配出貨」', { variant: 'warning' });
      }
    }
  } catch (error) {
    console.error('Error printing shipping document:', error);
    enqueueSnackbar('無法產生出貨單: ' + ((error as Error).message || '未知錯誤'), {
      variant: 'error',
    });
  }
}


// Updated handleShipOrder without full page refresh
// groupOrderIds: when shipping a combined group, all order ids in the group
// (primary first) so the total covers everything and secondaries ship together.
async function handleShipOrder(order: Sale, groupOrderIds?: string[]) {
  if (!order) return;

  const idsForTotal = (groupOrderIds && groupOrderIds.length > 0
    ? groupOrderIds
    : [order.order_id]).filter(Boolean) as string[];

  const orderItems = salesHistory?.filter(item => item.order_id && idsForTotal.includes(item.order_id)) || [];
  // 代收/出貨 amount must be NET of any redeemed 購物金 (store credit): sale_price is gross,
  // net_sale_price (sale_price − credit_applied) is what the customer still owes at pickup.
  // On a COMBINED shipment it must also exclude group members the customer already paid for
  // (card / LINE Pay) — otherwise the counter collects those a second time.
  const { cod, paid, total } = splitCodAmount(orderItems);
  let totalOrderAmount = paid > 0 && cod > 0 ? cod : total;

  // …and it can never exceed what the order says the customer owes (see codTotalCap).
  const codCap = codTotalCap(orderItems);
  if (codCap !== null && totalOrderAmount > codCap) {
    console.warn(
      `[cod-total-cap] ${idsForTotal.join(', ')}: outlet_sales gives ${totalOrderAmount} but order total is ${codCap}; using ${codCap}`
    );
    totalOrderAmount = codCap;
  }

  // Remember the group so confirmShipOrder can mark secondaries sent too.
  setShipGroupOrderIds(idsForTotal);

  // Initialize editable amount
  setEditableAmount(totalOrderAmount.toString());

  const hasTrackingInfo = order.tracking_number || order.payment_no || order.cvs_validation_no;

  if (hasTrackingInfo) {
    setCurrentOrder(order);
    setShippingDialogOpen(true);
    return;
  }

  // For 宅配 orders, go directly to shipping dialog
  if (order.delivery_method === 'shipping') {
    setCurrentOrder(order);
    setShippingDialogOpen(true);
    return;
  }

  if (order.ecpay_logistics_id && order.delivery_method === 'pickup') {
    try {
      enqueueSnackbar('正在獲取追蹤號碼...', { variant: 'info' });

      const response = await api.get(`/api/ecpay/logistics/tracking`, {
        params: {
          logisticsId: order.ecpay_logistics_id,
          orderId: order.order_id
        }
      });

      if (response.data && response.data.success) {
        const trackingUpdate = {
          tracking_number: response.data.trackingNumber,
          payment_no: response.data.paymentNo,
          cvs_validation_no: response.data.validationNo,
          ecpay_logistics_id: response.data.logisticsId
        };

        enqueueSnackbar(`已獲取追蹤號碼: ${response.data.trackingNumber}`, { 
          variant: 'success',
          autoHideDuration: 5000
        });

        // ✅ 即時更新畫面
        if (updateSaleInList && order.order_id) {
          updateSaleInList(order.order_id, trackingUpdate);
        }

        // ✅ 更新當前 dialog 中的資料
        setCurrentOrder({
          ...order,
          ...trackingUpdate
        });

        setShippingDialogOpen(true);
      } else {
        // 沒成功也開啟 dialog
        setCurrentOrder(order);
        setShippingDialogOpen(true);

        enqueueSnackbar(response.data?.message || '無法獲取追蹤號碼，訂單可能尚未準備好', {
          variant: 'warning'
        });
      }

    } catch (error) {
      console.error('Error getting tracking number:', error);
      setCurrentOrder(order);
      setShippingDialogOpen(true);
      enqueueSnackbar('獲取追蹤號碼時發生錯誤', { variant: 'error' });
    }
  } else {
    // 沒有 ECPay ID，直接開啟 dialog
    setCurrentOrder(order);
    setShippingDialogOpen(true);
  }
}


// Updated confirmShipOrder function in SalesHistoryTab.tsx
async function confirmShipOrder() {
  if (!currentOrder || !currentOrder.order_id) return;

  setProcessingShipping(true);

  try {
    // 宅配 orders: create a 順豐 TW domestic waybill and open the label in a new tab
    if (currentOrder.delivery_method === 'shipping') {
      // Tracking number to propagate to any combined-group secondaries
      let groupTracking: string | null = currentOrder.sf_waybill_no || currentOrder.hsinchu_tracking_number || null;
      // Parse shipping address from JSONB
      let addressStr = '';
      if (currentOrder.shipping_address) {
        if (typeof currentOrder.shipping_address === 'string') {
          try {
            const addr = JSON.parse(currentOrder.shipping_address);
            addressStr = [addr.city, addr.district, addr.address1].filter(Boolean).join('');
          } catch {
            addressStr = currentOrder.shipping_address;
          }
        } else if (typeof currentOrder.shipping_address === 'object') {
          const addr = currentOrder.shipping_address as any;
          addressStr = [addr.city, addr.district, addr.address1].filter(Boolean).join('');
        }
      }

      const recipientName = currentOrder.customer_name || currentOrder.customer_phone || '客戶';
      const recipientPhone = (currentOrder.customer_phone || '').replace(/^\+886/, '0').replace(/[\s-]/g, '');

      // Already has an SF waybill — just reopen the saved label
      if (currentOrder.sf_waybill_no) {
        groupTracking = currentOrder.sf_waybill_no;
        if (currentOrder.sf_label_url) {
          const win = window.open(currentOrder.sf_label_url, '_blank', 'noopener,noreferrer');
          if (!win) enqueueSnackbar('請允許彈出視窗以開啟順豐面單', { variant: 'warning' });
        }
        enqueueSnackbar(`順豐單已存在 (單號: ${currentOrder.sf_waybill_no})`, { variant: 'info' });
      // Legacy 新竹物流 shipment created before the SF switch — reprint that label
      } else if (currentOrder.hsinchu_tracking_number) {
        try {
          const labelResponse = await api.get(`/api/hsinchu/label/${currentOrder.hsinchu_tracking_number}`);
          if (labelResponse.data?.success && labelResponse.data.data?.labelImage) {
            displayShippingLabel(labelResponse.data.data.labelImage, {
              trackingNumber: currentOrder.hsinchu_tracking_number,
              recipientName,
              recipientAddress: addressStr
            });
            enqueueSnackbar(`出貨標籤已開啟 (追蹤號碼: ${currentOrder.hsinchu_tracking_number})`, { variant: 'success' });
          }
        } catch (err) {
          console.error('Error fetching existing label:', err);
          enqueueSnackbar('無法取得出貨標籤', { variant: 'error' });
        }
      } else {
        // Create a new 順豐 TW domestic waybill (replaced 新竹物流 for outlet 宅配
        // 2026-10). The backend persists sf_waybill_no / sf_label_url on the order,
        // so 列印 works after a refresh. Weight omitted → SF's declared 5kg.
        enqueueSnackbar('正在建立順豐宅配單...', { variant: 'info' });
        const sfResponse = await api.post(
          `/api/storefront/orders/${encodeURIComponent(currentOrder.order_id)}/sf-domestic/create`,
          {
            recipientName,
            recipientPhone,
            recipientAddress: addressStr,
            recipientEmail: currentOrder.customer_email || '',
          }
        );

        const result = sfResponse.data;
        if (!result?.success || !result.sfWaybillNo) {
          throw new Error(result?.error || '建立順豐宅配單失敗');
        }

        if (result.labelUrl) {
          const win = window.open(result.labelUrl, '_blank', 'noopener,noreferrer');
          if (!win) enqueueSnackbar('請允許彈出視窗以開啟順豐面單', { variant: 'warning' });
        } else {
          enqueueSnackbar('順豐單已建立，但未回傳面單連結', { variant: 'warning' });
        }

        enqueueSnackbar(`順豐宅配單已建立！單號: ${result.sfWaybillNo}`, { variant: 'success' });
        groupTracking = result.sfWaybillNo;

        if (updateSaleInList) {
          updateSaleInList(currentOrder.order_id, {
            sf_waybill_no: result.sfWaybillNo,
            sf_label_url: result.labelUrl ?? null,
            sf_invoice_url: result.invoiceUrl ?? null,
            sf_customer_order_no: result.customerOrderNo ?? null,
            sf_last_error: null,
            tracking_number: result.sfWaybillNo
          });
        }
      }

      // Mark as shipped
      await api.put(`/api/ecpay/${currentOrder.order_id}/status`, {
        status: 'SHIPPED',
        statusMessage: '已出貨'
      });

      // Mark as sent
      try {
        await api.post(`/api/outlet/admin/orders/${currentOrder.order_id}/sent`, {
          source: currentOrder.source || 'outlet',
          tracking_number: groupTracking,
          carrier: currentOrder.hsinchu_tracking_number && !currentOrder.sf_waybill_no ? '新竹物流' : '順豐'
        });
        if (updateSaleInList) {
          updateSaleInList(currentOrder.order_id, { is_sent: true });
        }
      } catch (sentError) {
        console.error('Error marking as sent:', sentError);
      }

      // Combined group: mark the folded-in secondary orders shipped too
      // (they ship inside this one parcel — no separate label is created).
      if (shipGroupOrderIds.length > 1) {
        await markGroupSecondariesSent(currentOrder.order_id, shipGroupOrderIds, groupTracking);
        enqueueSnackbar(`已將合併的 ${shipGroupOrderIds.length} 筆訂單一併標記為已發貨`, { variant: 'success' });
      }

      setProcessingShipping(false);
      setShippingDialogOpen(false);
      return;
    }

    // Step 1: Create logistics if needed (existing logic for pickup orders)
    if (currentOrder.delivery_method === 'pickup' && currentOrder.store_id && !currentOrder.ecpay_logistics_id) {
      try {
        const finalGoodsAmount = editableAmount ? parseInt(editableAmount) : 100;
        
        console.log(`Creating logistics with edited amount: ${finalGoodsAmount}`);

        const logisticsResponse = await api.post(`/api/ecpay/logistics/create-for-order`, {
          orderId: currentOrder.order_id,
          orderIds: shipGroupOrderIds.length ? shipGroupOrderIds : [currentOrder.order_id],
          storeId: currentOrder.store_id,
          customerName: currentOrder.customer_name || currentOrder.customer_phone || 'Customer',
          customerPhone: currentOrder.customer_phone || '0900000000',
          logisticsSubType: currentOrder.logistics_sub_type || determineDocumentType(currentOrder.store_id, currentOrder.logistics_sub_type),
          goodsAmount: finalGoodsAmount,
          storeName: currentOrder.store_name || '',
          source: currentOrder.source || 'admin'
        });

        if (logisticsResponse.data?.success) {
          currentOrder.ecpay_logistics_id = logisticsResponse.data.logisticsId;
          currentOrder.payment_no = logisticsResponse.data.paymentNo;
          currentOrder.cvs_validation_no = logisticsResponse.data.validationNo;
          
          // Update shipping status to 'prepared' when logistics is created
          try {
            await api.post('/api/outlet/mark-shipped', {
              orderId: currentOrder.order_id,
              orderSource: currentOrder.source || 'outlet',
              status: 'prepared',
              logistics_id: logisticsResponse.data.logisticsId,
              payment_no: logisticsResponse.data.paymentNo,
              cvs_store_id: currentOrder.store_id,
              store_name: currentOrder.store_name,
              logistics_subtype: currentOrder.logistics_sub_type
            });
          } catch (statusError) {
            console.error('Error updating shipping status to prepared:', statusError);
          }
        }
      } catch (err) {
        console.error('Create logistics failed:', err);
      }
    }

    // Step 2: Mark as shipped (existing logic with status update)
    const updateData = {
      status: 'SHIPPED',
      statusMessage: '已出貨',
      trackingNumber: currentOrder.tracking_number
    };

    const response = await api.put(`/api/ecpay/${currentOrder.order_id}/status`, updateData);

    if (response?.data?.success) {
      enqueueSnackbar('訂單已更新為已出貨狀態', { variant: 'success' });

      // Step 3: Get tracking number if needed (existing logic)
      if (!currentOrder.tracking_number && currentOrder.ecpay_logistics_id) {
        try {
          const trackingResponse = await api.get(`/api/ecpay/logistics/tracking`, {
            params: {
              logisticsId: currentOrder.ecpay_logistics_id,
              orderId: currentOrder.order_id
            }
          });

          if (trackingResponse.data?.success && trackingResponse.data.trackingNumber) {
            const trackingUpdate = {
              tracking_number: trackingResponse.data.trackingNumber,
              payment_no: trackingResponse.data.paymentNo,
              cvs_validation_no: trackingResponse.data.validationNo,
              ecpay_logistics_id: trackingResponse.data.logisticsId
            };

            if (updateSaleInList) {
              updateSaleInList(currentOrder.order_id, trackingUpdate);
            }

            enqueueSnackbar(`已獲取追蹤號碼: ${trackingResponse.data.trackingNumber}`, { variant: 'success' });
          }
        } catch (err) {
          console.error('Error getting tracking:', err);
        }
      }

      // NEW: Automatically mark as shipped (is_sent = true)
      try {
        await api.post(`/api/outlet/admin/orders/${currentOrder.order_id}/sent`, {
          source: currentOrder.source || 'outlet'
        });
        
        // Update UI to show as shipped
        if (updateSaleInList) {
          updateSaleInList(currentOrder.order_id, { is_sent: true });
        }
        
        enqueueSnackbar('訂單已自動標記為已發貨', { variant: 'success' });
      } catch (sentError) {
        console.error('Error marking as sent:', sentError);
        enqueueSnackbar('警告: 出貨成功但無法自動更新發貨狀態', { variant: 'warning' });
      }

      // Combined group: mark the folded-in secondary orders shipped too
      // (they ship inside this one parcel — no separate label is created).
      if (shipGroupOrderIds.length > 1) {
        const groupTracking = currentOrder.tracking_number || currentOrder.payment_no || null;
        await markGroupSecondariesSent(currentOrder.order_id, shipGroupOrderIds, groupTracking);
        enqueueSnackbar(`已將合併的 ${shipGroupOrderIds.length} 筆訂單一併標記為已發貨`, { variant: 'success' });
      }

    } else {
      throw new Error(response?.data?.message || '更新出貨狀態失敗');
    }

  } catch (error) {
    console.error('Error shipping order:', error);
    enqueueSnackbar('出貨處理失敗: ' + ((error as Error).message || '未知錯誤'), {
      variant: 'error'
    });
  } finally {
    setProcessingShipping(false);
    setShippingDialogOpen(false);
    setShipGroupOrderIds([]);
  }
}

// Open refund dialog
const openRefundDialog = (
  orderId: string,
  tradeNo: string,
  maxAmount: number,
  paymentMethod: 'credit_card' | 'line_pay' = 'credit_card'
) => {
  setRefundOrderId(orderId);
  setRefundTradeNo(tradeNo);
  setRefundMaxAmount(maxAmount);
  setRefundAmount(String(maxAmount));
  setRefundFullAmount(true);
  setRefundPaymentMethod(paymentMethod);
  setRefundDialogOpen(true);
};

// Handle saving order item notes (key = "orderId::itemKey")
const handleSaveNotes = async (compositeKey: string) => {
  if (!compositeKey) return;
  const [orderId, itemKey] = compositeKey.split('::');
  if (!orderId || !itemKey) return;
  setSavingNotes(true);
  try {
    await api.put('/api/products/order-item-notes', {
      orderId,
      itemKey,
      notes: editingNotesValue
    });
    setOrderItemNotes(prev => ({ ...prev, [compositeKey]: editingNotesValue }));
    setEditingNotesKey(null);
    enqueueSnackbar('備註已儲存', { variant: 'success' });
  } catch (error) {
    console.error('Error saving notes:', error);
    enqueueSnackbar('儲存備註失敗', { variant: 'error' });
  } finally {
    setSavingNotes(false);
  }
};

// Handle refund submission
const handleRefund = async () => {
  const amount = parseInt(refundAmount, 10);
  if (!amount || amount <= 0) {
    enqueueSnackbar('請輸入有效的退刷金額', { variant: 'warning' });
    return;
  }
  if (amount > refundMaxAmount) {
    enqueueSnackbar(`退刷金額不得超過訂單金額 $${refundMaxAmount}`, { variant: 'warning' });
    return;
  }

  setProcessingRefund(true);
  try {
    // LINE Pay and ECPay use separate refund endpoints. The LINE Pay endpoint
    // returns { success, isFullRefund, status, refundedTotal, ... }; the ECPay
    // endpoint returns { success, rtnMsg }. Normalize both into a single UI flow.
    const endpoint = refundPaymentMethod === 'line_pay'
      ? `/api/linepay/refund/${refundOrderId}`
      : `/api/ecpay/payment/refund/${refundOrderId}`;
    const response = await api.post(endpoint, { refundAmount: amount });

    if (response.data.success) {
      enqueueSnackbar(`退刷成功 - 金額: $${amount}`, { variant: 'success' });
      if (updateSaleInList) {
        if (refundPaymentMethod === 'line_pay') {
          // LINE Pay flow returns the new cumulative status. Mirror it locally
          // so the row's chip flips immediately without a refetch.
          const isFull = response.data.isFullRefund;
          updateSaleInList(refundOrderId, {
            linepay_status: response.data.status || (isFull ? 'REFUNDED' : 'PARTIALLY_REFUNDED'),
            linepay_refunded_amount: response.data.refundedTotal,
          });
        } else {
          updateSaleInList(refundOrderId, { payment_status: 'REFUNDED' });
        }
      }
      setRefundDialogOpen(false);
    } else {
      enqueueSnackbar(`退刷失敗: ${response.data.rtnMsg || '未知錯誤'}`, { variant: 'error' });
    }
  } catch (error: any) {
    const errMsg = error.response?.data?.message || error.message || '未知錯誤';
    enqueueSnackbar(`退刷失敗: ${errMsg}`, { variant: 'error' });
  } finally {
    setProcessingRefund(false);
  }
};

// Open the combine dialog scoped to the clicked order's customer + destination.
const openCombineDialog = (anchor: Sale) => {
  const phone = normalizePhone(anchor.customer_phone);
  const destK = destinationKey(anchor);
  const candidates = (customerUnshippedOutlet[phone] || []).filter(
    (s) => !!s.delivery_method && destinationKey(s) === destK
  );
  // Ensure the anchor itself is in the list even if some index lagged.
  if (anchor.order_id && !candidates.find((c) => c.order_id === anchor.order_id)) {
    candidates.unshift(anchor);
  }
  if (candidates.length < 2) {
    enqueueSnackbar('找不到可合併的同客戶訂單', { variant: 'info' });
    return;
  }
  setCombineCandidates(candidates);
  setCombineSelectedIds(candidates.map((c) => c.order_id as string));
  // Default primary: an order that already has a logistics label, else the anchor.
  const withLogistics = candidates.find((c) => c.ecpay_logistics_id);
  setCombinePrimaryId((withLogistics || anchor).order_id as string);
  setCombineDialogOpen(true);
};

const toggleCombineSelection = (orderId: string) => {
  const next = combineSelectedIds.includes(orderId)
    ? combineSelectedIds.filter((id) => id !== orderId)
    : [...combineSelectedIds, orderId];
  setCombineSelectedIds(next);
  // Keep the primary valid — if we just unchecked it, move to the first remaining.
  if (!next.includes(combinePrimaryId)) {
    setCombinePrimaryId(next[0] || '');
  }
};

const confirmCombine = async () => {
  if (combineSelectedIds.length < 2) {
    enqueueSnackbar('請至少選擇 2 筆訂單', { variant: 'warning' });
    return;
  }
  const primaryOrderId =
    combinePrimaryId && combineSelectedIds.includes(combinePrimaryId)
      ? combinePrimaryId
      : combineSelectedIds[0];
  const orderIds = combineSelectedIds.filter((id) => id !== primaryOrderId);

  setCombineProcessing(true);
  try {
    const resp = await api.post('/api/outlet/admin/orders/merge', { primaryOrderId, orderIds, createdBy: 'admin' });
    const data = resp?.data || {};
    const refundCount = (data.waivedRefunds || []).length;
    const refundNote = refundCount > 0 ? `（退購物金至 ${refundCount} 筆已付款訂單）` : '';
    let msg = `已合併 ${combineSelectedIds.length} 筆訂單，將以單一包裹出貨`;
    if (data.shippingWaived) {
      msg += `，已免運費${refundNote}`;
    } else if (data.duplicateShippingRemoved > 0) {
      msg += `，運費僅收取一次${refundNote}`;
    }
    enqueueSnackbar(msg, { variant: 'success' });
    setCombineDialogOpen(false);
    // Reload so merged_into is reflected and cards fold together.
    if (typeof fetchSalesHistory === 'function') fetchSalesHistory();
  } catch (error: any) {
    const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error.message;
    enqueueSnackbar(`合併失敗: ${msg}`, { variant: 'error' });
  } finally {
    setCombineProcessing(false);
  }
};

const handleUnmerge = async (groupId: string) => {
  try {
    await api.post(`/api/outlet/admin/orders/${encodeURIComponent(groupId)}/unmerge`);
    enqueueSnackbar('已取消合併', { variant: 'success' });
    if (typeof fetchSalesHistory === 'function') fetchSalesHistory();
  } catch (error: any) {
    const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error.message;
    enqueueSnackbar(`取消合併失敗: ${msg}`, { variant: 'error' });
  }
};

// After the primary order of a combined group ships, mark the folded-in
// secondary orders as shipped too (no separate label is created for them).
async function markGroupSecondariesSent(
  primaryOrderId: string,
  groupOrderIds: string[],
  tracking?: string | null
) {
  const secondaries = (groupOrderIds || []).filter((id) => id && id !== primaryOrderId);
  for (const sid of secondaries) {
    try {
      await api.put(`/api/ecpay/${sid}/status`, { status: 'SHIPPED', statusMessage: '已出貨（合併出貨）' });
    } catch (e) {
      console.error('combine: failed to set ecpay status for', sid, e);
    }
    try {
      await api.post(`/api/outlet/admin/orders/${sid}/sent`, {
        source: 'outlet',
        tracking_number: tracking || null,
        carrier: null,
      });
    } catch (e) {
      console.error('combine: failed to mark sent for', sid, e);
    }
    if (updateSaleInList) {
      updateSaleInList(sid, { is_sent: true, ...(tracking ? { tracking_number: tracking } : {}) });
    }
  }
}

// Build a UnifiedOrder-shaped object the SFInternationalShippingModal understands
// from an outlet shipment group (the modal was written for ReceivingQueuePage).
function buildSFModalOrder(primary: Sale, items: Sale[]): any {
  const orderId = primary.order_id;
  const totalAmount = items.reduce(
    (sum, it) => sum + Math.floor(parseFloat(it.sale_price || '0')),
    0
  );
  return {
    orderId,
    orderNumber: orderId,
    platform: 'storefront',
    deliveryMethod: 'intl',
    customerName: primary.customer_name || '',
    customerPhone: primary.order_customer_phone || primary.customer_phone || '',
    customerEmail: primary.customer_email || '',
    shippingAddress: primary.shipping_address || '',
    totalAmount,
    items: items.map((it) => ({
      id: it.id || it.outlet_item_id,
      productName: it.title || it.sku || '商品',
      variantOrSpec: it.variant_details || '',
      sku: it.display_sku || it.sku || '',
      quantity: Number(it.quantity) || 1,
      imageUrl: it.image_url,
      originalItem: { cart_discount_price: parseFloat(it.sale_price || '0') },
    })),
    // The modal reads SF state + receiver pre-fill from `.original`.
    original: {
      order_id: orderId,
      customer_name: primary.customer_name,
      customer_phone: primary.order_customer_phone || primary.customer_phone,
      customer_email: primary.customer_email,
      shipping_address: primary.shipping_address,
      shipping_country: primary.shipping_country || 'HK',
      sf_waybill_no: primary.sf_waybill_no,
      sf_label_url: primary.sf_label_url,
      sf_invoice_url: primary.sf_invoice_url,
      sf_customer_order_no: primary.sf_customer_order_no,
      sf_cancelled_at: primary.sf_cancelled_at,
      sf_last_error: primary.sf_last_error,
    },
  };
}

// Intl ship button → open the SF modal for this order group. SF-carried regions
// (HK/MO) only. SG books through Parxl, which needs a scale weight before it can
// price the parcel — that flow lives on the Receiving Queue, not here.
const handleShipHK = (primary: Sale, items: Sale[]) => {
  if (!isSfIntlOrder(primary)) {
    const region = intlRegionOf(primary);
    enqueueSnackbar(
      region?.carrier === 'parxl'
        ? `${region.nameZh}訂單由 ${region.carrierLabel} 配送，請至「收貨/出貨」頁點「${region.nameZh}出貨」建單（需填包裹重量）`
        : `${region?.nameZh || '此'}訂單不適用順豐出貨`,
      { variant: 'warning', autoHideDuration: 8000 }
    );
    return;
  }
  setSfModalOrder(buildSFModalOrder(primary, items));
};

// SF waybill created/updated → mirror the new state onto every row of the order
// and mark it sent, without a full refetch.
const handleSFShippingPrepared = (data: SFShippingResult) => {
  const orderId = sfModalOrder?.orderId;
  if (!orderId) return;
  if (updateSaleInList) {
    updateSaleInList(orderId, {
      is_sent: true,
      tracking_number: data.sfWaybillNo,
      sf_waybill_no: data.sfWaybillNo,
      sf_label_url: data.labelUrl ?? null,
      sf_invoice_url: data.invoiceUrl ?? null,
      sf_customer_order_no: data.customerOrderNo ?? null,
      sf_last_error: null,
    });
  }
};

const toggleOrderSelection = (orderId: string) => {
  setSelectedOrders(prev => {
    if (prev.includes(orderId)) {
      return prev.filter(id => id !== orderId);
    } else {
      return [...prev, orderId];
    }
  });
};

const selectAllVisibleOrders = () => {
  const eligibleOrders = Object.entries(groupSalesByOrder(filteredSalesHistory))
    .filter(([orderId, orderItems]) => {
      const firstItem = orderItems[0];
      return firstItem.delivery_method === 'pickup' && 
             firstItem.store_id && 
             firstItem.ecpay_logistics_id;
    })
    .map(([orderId]) => orderId);
  
  setSelectedOrders(eligibleOrders);
};

const clearAllSelections = () => {
  setSelectedOrders([]);
};

// Batch print function
const handleBatchPrint = async () => {
  if (selectedOrders.length === 0) {
    enqueueSnackbar('請選擇要列印的訂單', { variant: 'warning' });
    return;
  }
  
  setBatchPrintDialogOpen(true);
};

const confirmBatchPrint = async () => {
  try {
    setBatchPrintLoading(true);
    
    // Group selected orders by logistics sub type for separate printing
    const ordersByType: Record<string, string[]> = {};
    const orderDetails: Record<string, Sale> = {};
    
    // Collect order details for selected orders
    Object.entries(groupSalesByOrder(filteredSalesHistory)).forEach(([orderId, orderItems]) => {
      if (selectedOrders.includes(orderId)) {
        const firstItem = orderItems[0];
        if (firstItem.delivery_method === 'pickup' && firstItem.store_id && firstItem.ecpay_logistics_id) {
          const logisticsType = firstItem.logistics_sub_type || 'UNIMARTC2C';
          
          if (!ordersByType[logisticsType]) {
            ordersByType[logisticsType] = [];
          }
          
          ordersByType[logisticsType].push(orderId);
          orderDetails[orderId] = firstItem;
        }
      }
    });
    
    console.log('Orders grouped by logistics type:', ordersByType);
    
    // Print each group separately
    for (const [logisticsType, orderIds] of Object.entries(ordersByType)) {
      try {
        console.log(`Batch printing ${orderIds.length} orders with logistics type: ${logisticsType}`);
        
        // Collect all the logistics data for this batch
        const logisticsIds: string[] = [];
        const paymentNos: string[] = [];
        const validationNos: string[] = [];
        
        orderIds.forEach(orderId => {
          const order = orderDetails[orderId];
          if (order.ecpay_logistics_id && order.payment_no) {
            logisticsIds.push(order.ecpay_logistics_id);
            paymentNos.push(order.payment_no);
            
            // Handle validation numbers based on logistics type
            if (logisticsType === 'FAMIC2C') {
              // Family Mart doesn't need validation number in the array
              // But we still need to maintain array length consistency
              validationNos.push('');
            } else {
              // For 7-ELEVEN and others, use cvs_validation_no or parse from payment_no
              if (order.cvs_validation_no) {
                validationNos.push(order.cvs_validation_no);
              } else if (order.payment_no && order.payment_no.length >= 4) {
                validationNos.push(order.payment_no.slice(-4));
              } else {
                validationNos.push('');
              }
            }
          }
        });
        
        // Call batch print API
        const response = await api.post('/api/ecpay/logistics/batch-print', {
          logisticsIds: logisticsIds.join(','),
          paymentNos: paymentNos.join(','),
          validationNos: validationNos.join(','),
          documentType: logisticsType,
          orderIds: orderIds // For logging purposes
        });
        
        if (response.data) {
          // Open the print document in a new window
          const blob = new Blob([response.data], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          
          // Clean up
          setTimeout(() => URL.revokeObjectURL(url), 100);
        }
        
        enqueueSnackbar(
          `${orderIds.length} 張 ${logisticsType === 'FAMIC2C' ? '全家' : '7-ELEVEN'} 出貨單已開啟`, 
          { variant: 'success' }
        );
        
      } catch (error) {
        console.error(`Error batch printing ${logisticsType}:`, error);
        
      }
    }
    
    // Clear selections after successful print
    setSelectedOrders([]);
    
  } catch (error) {
    console.error('Batch print error:', error);
    enqueueSnackbar('批次列印失敗', { variant: 'error' });
  } finally {
    setBatchPrintLoading(false);
    setBatchPrintDialogOpen(false);
  }
};

// Add this component for the batch actions toolbar
const BatchActionsToolbar = () => {
  const eligibleOrdersCount = Object.entries(groupSalesByOrder(filteredSalesHistory))
    .filter(([orderId, orderItems]) => {
      const firstItem = orderItems[0];
      return firstItem.delivery_method === 'pickup' && 
             firstItem.store_id && 
             firstItem.ecpay_logistics_id;
    }).length;
  
  
  return (
    <Box sx={{ 
      mb: 2, 
      p: 2, 
      bgcolor: 'background.paper', 
      border: 1, 
      borderColor: 'divider',
      borderRadius: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="body2">
          批次操作: 已選擇 {selectedOrders.length} / {eligibleOrdersCount} 個可列印訂單
        </Typography>
        <Button 
          size="small" 
          onClick={selectAllVisibleOrders}
          disabled={selectedOrders.length === eligibleOrdersCount}
        >
          全選
        </Button>
        <Button 
          size="small" 
          onClick={clearAllSelections}
          disabled={selectedOrders.length === 0}
        >
          清除選擇
        </Button>
      </Box>
      
      <Button
        variant="contained"
        startIcon={<PrintIcon />}
        onClick={handleBatchPrint}
        disabled={selectedOrders.length === 0}
        color="primary"
      >
        批次列印 ({selectedOrders.length})
      </Button>
    </Box>
  );
};

// Add checkbox to each order header (modify your existing order rendering)
const OrderHeaderWithCheckbox = ({ orderId, orderItems, isSelected, onToggle }: {
  orderId: string;
  orderItems: Sale[];
  isSelected: boolean;
  onToggle: (orderId: string) => void;
}) => {
  const firstItem = orderItems[0];
  const canPrint = firstItem.delivery_method === 'pickup' && 
                   firstItem.store_id && 
                   firstItem.ecpay_logistics_id;
  
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {canPrint && (
          <Checkbox
            checked={isSelected}
            onChange={() => onToggle(orderId)}
            size="small"
            color="primary"
          />
        )}
        <Typography variant="h6">
          訂單 #{orderId}
        </Typography>
        <Chip 
          label={firstItem.source === 'shopee' ? "Shopee" : "Outlet"} 
          size="small" 
          color={firstItem.source === 'shopee' ? "primary" : "secondary"}
          variant="outlined"
        />
        {/* Add payment method chip here */}
        {(() => {
          const paymentInfo = getPaymentMethodInfo(firstItem.payment_method);
          return (
            <Chip 
              label={paymentInfo.label}
              size="small"
              color={paymentInfo.color}
              variant="filled"
              icon={<span style={{ fontSize: '14px' }}>{paymentInfo.icon}</span>}
            />
          );
        })()}
        {/* ... rest of your existing header content ... */}
      </Box>
      {/* ... rest of your existing actions ... */}
    </Box>
  );
};

function getProductKey(sku: string, productName: string, variantDetails: string): string {
  if (sku && sku.trim()) {
    return sku.trim();
  }
  return `${productName}|${variantDetails || 'default'}`.toLowerCase().trim();
}


async function refreshSingleOrderData(orderId: string) {
  try {
    // Make a targeted API call to get just this order's updated data
    const response = await api.get(`/api/outlet/admin/orders/${orderId}/details`);
    
    if (response.data && response.data.success) {
      const updatedOrderItems = response.data.orderItems || [response.data.order];
      
      // Update each item for this order in the sales list
      updatedOrderItems.forEach((updatedItem: Sale) => {
        if (updateSaleInList && updatedItem.order_id) {
          updateSaleInList(updatedItem.order_id, updatedItem);
        }
      });
      
      return updatedOrderItems[0]; // Return the first item for the dialog
    }
  } catch (error) {
    console.error('Error refreshing single order:', error);
    
    // If single order refresh fails, try to update from tracking API response
    try {
      const trackingResponse = await api.get(`/api/ecpay/logistics/tracking`, {
        params: { orderId }
      });
      
      if (trackingResponse.data && trackingResponse.data.success) {
        const trackingData = {
          tracking_number: trackingResponse.data.trackingNumber,
          payment_no: trackingResponse.data.paymentNo,
          cvs_validation_no: trackingResponse.data.validationNo,
          ecpay_logistics_id: trackingResponse.data.logisticsId
        };
        
        if (updateSaleInList) {
          updateSaleInList(orderId, trackingData);
        }
        
        return trackingData;
      }
    } catch (trackingError) {
      console.error('Error getting tracking data:', trackingError);
    }
  }
  
  return null;
}

  // Safely check if an order ID is in hidden orders array
  function isOrderHidden(orderId: string | undefined, hiddenOrdersArray: string[] | any[], isHiddenFlag: boolean) {
    if (isHiddenFlag) return true;
    if (!orderId) return false;
    if (!Array.isArray(hiddenOrdersArray)) return false;
    return hiddenOrdersArray.includes(orderId);
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Sales History</Typography>
        <Box>
          <Button 
            variant="outlined" 
            color="info"
            onClick={() => {
              if (typeof setShowHidden === 'function') {
                setShowHidden(!showHidden);
              }
            }}
            sx={{ mr: 1 }}
          >
            {showHidden ? "Hide Completed Orders" : "Show All Orders"}
          </Button>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={() => {
              if (typeof fetchSalesHistory === 'function') {
                fetchSalesHistory();
              }
            }}
            disabled={loadingSales}
            sx={{ ml: 1 }}
          >
            Refresh
          </Button>
        </Box>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by Order ID, Item, SKU, Tag, or Customer..."
          variant="outlined"
          size="small"
          value={salesHistorySearch}
          onChange={(e) => {
            if (typeof setSalesHistorySearch === 'function') {
              setSalesHistorySearch(e.target.value);
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: salesHistorySearch ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => {
                  if (typeof setSalesHistorySearch === 'function') {
                    setSalesHistorySearch('');
                  }
                }}>
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />
      </Box>
       <BatchActionsToolbar />
       
      
      {loadingSales ? (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
    <CircularProgress />
  </Box>
) : (!Array.isArray(filteredSalesHistory) || filteredSalesHistory.length === 0) ? (
  <Alert severity="info" sx={{ mt: 2 }}>
    {salesHistorySearch ? "No matching sales records found." : "No sales records found."}
  </Alert>
) : (() => {
  const allGroups = buildMergeGroups(filteredSalesHistory);
  const pageCount = Math.max(1, Math.ceil(allGroups.length / GROUPS_PER_PAGE));
  const safePage = Math.min(page, pageCount);
  const pagedGroups = allGroups.slice((safePage - 1) * GROUPS_PER_PAGE, safePage * GROUPS_PER_PAGE);
  const showPager = allGroups.length > GROUPS_PER_PAGE;
  const rangeStart = (safePage - 1) * GROUPS_PER_PAGE + 1;
  const rangeEnd = Math.min(safePage * GROUPS_PER_PAGE, allGroups.length);
  return (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {showPager && (
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          顯示第 {rangeStart}–{rangeEnd} 筆，共 {allGroups.length} 筆訂單
        </Typography>
        <Pagination count={pageCount} page={safePage} onChange={(_, v) => setPage(v)} color="primary" size="small" />
      </Box>
    )}
    {pagedGroups.map((group) => {
      // orderId / orderItems / firstItem keep their original meaning but now
      // reflect the COMBINED group: orderId is the primary order (the one we ship
      // under), orderItems is every row across the group, firstItem is the
      // primary's representative row. Existing per-order logic below is unchanged.
      const orderId = group.groupId;
      const orderItems = group.items;
      const firstItem = group.primaryItem;
      const combinableSiblings = combinableSiblingsFor(firstItem);
      const isHidden = isOrderHidden(firstItem.order_id, hiddenOrders, !!firstItem.is_hidden);
      const isShopee = firstItem.source === 'shopee';
      
      // Calculate order total (NET of 購物金 — what the customer actually pays)
      const orderTotal = sumNetAmount(orderItems);
      const originalTotal = orderItems.reduce((sum, item) => sum + Math.floor(parseFloat(item.original_price || '0')), 0);
      // 購物金 (store credit) redeemed on this order — drives the badge. Prefer the order-level
      // value (flags even old orders whose lines were never reconciled); fall back to the sum
      // of per-line credit_applied. 0 for orders/Shopee rows that used no credit.
      const orderCreditUsed = Math.floor(
        parseFloat(firstItem.order_store_credit_used || '0') ||
        orderItems.reduce((s, it) => s + (parseFloat(`${it.credit_applied ?? '0'}`) || 0), 0)
      );
      
      // Get delivery method information
      const deliveryInfo = getDeliveryMethodInfo(
        firstItem.delivery_method || 'pickup',
        {
          logisticsSubType: firstItem.logistics_sub_type,
          storeId: firstItem.store_id
        }
      );
      
      return (
        <Paper 
          key={orderId} 
          elevation={2} 
          sx={{ 
            p: 2, 
            opacity: isHidden ? 0.7 : 1,
            bgcolor: isHidden ? '#f9f9f9' : 'background.paper',
            border: '1px solid',
            borderColor: isHidden ? 'grey.300' : 'divider'
          }}
        >
          {/* Order Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* ADD CHECKBOX HERE */}
              {firstItem.delivery_method === 'pickup' && 
              firstItem.store_id && 
              firstItem.ecpay_logistics_id && (
                <Checkbox
                  checked={selectedOrders.includes(orderId)}
                  onChange={() => toggleOrderSelection(orderId)}
                  size="small"
                  color="primary"
                />
              )}
              <Typography variant="h6">
                訂單 #{orderId}
              </Typography>
              <Chip
                label={isShopee ? "Shopee" : "Outlet"}
                size="small"
                color={isShopee ? "primary" : "secondary"}
                variant="outlined"
              />
              {group.isMerged && (
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 600 }}>
                        合併出貨（同一客戶、同一取貨點）：
                      </Typography>
                      {group.orderIds.map((oid) => (
                        <Typography key={oid} variant="caption" display="block">
                          #{oid}{oid === group.groupId ? ' (主單)' : ''}
                        </Typography>
                      ))}
                    </Box>
                  }
                  arrow
                >
                  <Chip
                    icon={<CallMergeIcon />}
                    label={`合併出貨 · ${group.orderIds.length} 筆`}
                    size="small"
                    color="info"
                    variant="filled"
                    sx={{ fontWeight: 600 }}
                  />
                </Tooltip>
              )}
              {isHidden && (
                <Chip
                  label="已隱藏"
                  size="small"
                  color="default"
                  variant="filled"
                />
              )}
            </Box>
            
            
            {/* Order Actions */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* Intl 順豐 Button — SF Express International for HK/MO orders */}
              {isIntlSale(firstItem) && (
                <Button
                  size="small"
                  color={firstItem.sf_waybill_no ? 'success' : 'info'}
                  variant="contained"
                  startIcon={<LocalShippingIcon />}
                  onClick={() => handleShipHK(firstItem, orderItems)}
                >
                  {firstItem.sf_waybill_no
                    ? '順豐單'
                    : `${intlRegionOf(firstItem)?.nameZh || '香港'}出貨 (${intlRegionOf(firstItem)?.carrierLabel || '順豐'})`}
                </Button>
              )}

              {/* Intl 順豐 Print Button — opens the saved SF label */}
              {isIntlSale(firstItem) && firstItem.sf_label_url && (
                <IconButton
                  size="small"
                  color="secondary"
                  title="列印順豐出貨單"
                  onClick={() => window.open(firstItem.sf_label_url as string, '_blank', 'noopener,noreferrer')}
                >
                  <PrintIcon />
                </IconButton>
              )}

              {/* Ship Button - Show for non-sent orders with pickup or shipping delivery */}
              {!firstItem.is_sent && !isIntlSale(firstItem) && (firstItem.delivery_method === 'pickup' || firstItem.delivery_method === 'shipping') && (
                <Button
                  size="small"
                  color={firstItem.delivery_method === 'shipping' ? "warning" : (firstItem.ecpay_logistics_id ? "primary" : "inherit")}
                  variant="contained"
                  startIcon={firstItem.delivery_method === 'shipping' ? <HomeIcon /> : <LocalShippingIcon />}
                  onClick={() => handleShipOrder(firstItem, group.orderIds)}
                >
                  {firstItem.delivery_method === 'shipping'
                    ? (group.isMerged ? `宅配出貨 (${group.orderIds.length}筆)` : '宅配出貨')
                    : (group.isMerged ? `出貨 (${group.orderIds.length}筆)` : '出貨')}
                </Button>
              )}
              
              {/* Print Button - Show for pickup orders with data OR 宅配 orders with tracking */}
              {((firstItem.delivery_method === 'pickup' && firstItem.store_id && firstItem.ecpay_logistics_id) ||
                (firstItem.delivery_method === 'shipping' && !isIntlSale(firstItem) && (firstItem.sf_waybill_no || firstItem.hsinchu_tracking_number || firstItem.tracking_number))) && (
                <IconButton
                  size="small"
                  color="secondary"
                  title="列印出貨單"
                  onClick={() => handlePrintShippingDocument(firstItem)}
                >
                  <PrintIcon />
                </IconButton>
              )}
              
              {/* Refund Button — credit card (ECPay) */}
              {firstItem.payment_method === 'credit_card' && firstItem.payment_status === 'SUCCESS' && (
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  onClick={() => openRefundDialog(
                    orderId,
                    firstItem.payment_trade_no || '',
                    orderTotal,
                    'credit_card'
                  )}
                >
                  退刷
                </Button>
              )}
              {/* Refund Button — LINE Pay. Allowed while transaction is CONFIRMED
                  (never refunded) or PARTIALLY_REFUNDED (still has remaining balance). */}
              {firstItem.payment_method === 'line_pay'
                && (firstItem.linepay_status === 'CONFIRMED'
                    || firstItem.linepay_status === 'PARTIALLY_REFUNDED') && (
                <Button
                  size="small"
                  color="warning"
                  variant="outlined"
                  onClick={() => {
                    const total = firstItem.linepay_amount ?? orderTotal;
                    const refunded = firstItem.linepay_refunded_amount ?? 0;
                    const remaining = Math.max(0, total - refunded);
                    openRefundDialog(
                      orderId,
                      firstItem.linepay_transaction_id || '',
                      remaining,
                      'line_pay'
                    );
                  }}
                >
                  退刷 (LINE Pay)
                </Button>
              )}
              {/* Refunded indicators — covers both ECPay and LINE Pay */}
              {firstItem.payment_status === 'REFUNDED' && (
                <Chip label="已退刷" size="small" color="warning" variant="filled" />
              )}
              {firstItem.linepay_status === 'REFUNDED' && (
                <Chip label="已退刷 (LINE Pay)" size="small" color="warning" variant="filled" />
              )}
              {firstItem.linepay_status === 'PARTIALLY_REFUNDED' && (
                <Chip
                  label={`部分退刷 ${firstItem.linepay_refunded_amount ?? 0}/${firstItem.linepay_amount ?? '?'}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}

              {/* Unmerge Button — only for combined groups */}
              {group.isMerged && (
                <Tooltip title="取消合併（拆回各別訂單）">
                  <IconButton
                    size="small"
                    color="default"
                    onClick={() => handleUnmerge(group.groupId)}
                  >
                    <CallSplitIcon />
                  </IconButton>
                </Tooltip>
              )}

              {/* Relist Button — return this dead order's unshipped outlet items
                  to the shelf (void sale + keep bin + hide order). Only for
                  non-Shopee, not-yet-shipped orders (garment still on shelf). */}
              {!isShopee && !firstItem.is_sent && (
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => relistOrderUnits(firstItem.order_id, firstItem.is_sent)}
                  title="退回架上（取消佔用、重新上架、保留貨架格）"
                >
                  <ReplayIcon />
                </IconButton>
              )}

              {/* Show/Hide Button */}
              <IconButton
                size="small"
                color={isHidden ? "primary" : "default"}
                onClick={() => toggleHideOrder(firstItem.order_id, firstItem.source, isHidden, group.orderIds)}
                title={group.isMerged
                  ? (isHidden ? `顯示這 ${group.orderIds.length} 筆合併訂單` : `隱藏這 ${group.orderIds.length} 筆合併訂單`)
                  : (isHidden ? "顯示訂單" : "隱藏訂單")}
              >
                {isHidden ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </Box>
          </Box>

          {/* Same-customer combine detection — only for unshipped outlet orders
              that share a delivery destination with this one. */}
          {!group.isMerged && combinableSiblings.length > 0 && (
            <Alert
              severity="info"
              icon={<CallMergeIcon fontSize="inherit" />}
              sx={{ mb: 2 }}
              action={
                <Button
                  color="info"
                  size="small"
                  variant="contained"
                  startIcon={<CallMergeIcon />}
                  onClick={() => openCombineDialog(firstItem)}
                >
                  合併出貨
                </Button>
              }
            >
              此客戶另有 {combinableSiblings.length} 筆未出貨訂單（相同
              {firstItem.delivery_method === 'shipping' ? '宅配地址' : '取貨門市'}），可合併為一次出貨。
            </Alert>
          )}

          {/* Order Info */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">客戶資訊</Typography>
              {editingNameOrderId === firstItem.order_id ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <TextField
                    size="small"
                    autoFocus
                    value={editingNameValue}
                    onChange={(e) => setEditingNameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') saveCustomerName(firstItem.order_id || '', firstItem.source);
                      if (e.key === 'Escape') cancelEditName();
                    }}
                    disabled={savingName}
                    placeholder="輸入客戶姓名"
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => saveCustomerName(firstItem.order_id || '', firstItem.source)}
                    disabled={savingName}
                    title="儲存"
                  >
                    {savingName ? <CircularProgress size={16} /> : <CheckCircleIcon fontSize="small" />}
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={cancelEditName}
                    disabled={savingName}
                    title="取消"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body1">
                    {firstItem.customer_name || firstItem.customer_phone || firstItem.customer_email || 'N/A'}
                  </Typography>
                  {firstItem.source !== 'shopee' && (
                    <Tooltip title="修改客戶姓名">
                      <IconButton
                        size="small"
                        onClick={() => startEditName(firstItem.order_id || '', firstItem.customer_name || '')}
                        sx={{ ml: 0.5 }}
                      >
                        <SearchIcon fontSize="small" sx={{ transform: 'scale(0.85)', display: 'none' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'primary.main', textDecoration: 'underline' }}>
                          修改
                        </Typography>
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            </Box>
            
            <Box>
  <Typography variant="body2" color="text.secondary">付款方式</Typography>
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
    {(() => {
      const paymentInfo = getPaymentMethodInfo(firstItem.payment_method, firstItem.payment_status);
      return (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <span style={{ marginRight: 8 }}>{paymentInfo.icon}</span>
            <Chip 
              label={paymentInfo.label}
              size="small"
              color={paymentInfo.color}
              variant="outlined"
            />
          </Box>
          
          {/* Show additional payment status info for credit cards */}
          {firstItem.payment_method === 'credit_card' && firstItem.payment_status && (
            <Box sx={{ mt: 0.5 }}>
              {firstItem.payment_status === 'SUCCESS' && firstItem.payment_date && (
                <Typography variant="caption" color="text.secondary">
                  付款時間: {new Date(firstItem.payment_date).toLocaleString()}
                </Typography>
              )}
              {firstItem.payment_trade_no && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  交易編號: {firstItem.payment_trade_no}
                </Typography>
              )}
            </Box>
          )}
        </>
      );
    })()}
  </Box>
</Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">訂單金額</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography variant="h6">${orderTotal}</Typography>
                {originalTotal > orderTotal && (
                  <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                    ${originalTotal}
                  </Typography>
                )}
              </Box>
              {orderCreditUsed > 0 && (
                <Chip
                  size="small"
                  label={`折抵購物金 $${orderCreditUsed}`}
                  color="secondary"
                  variant="outlined"
                  sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
            
            <Box>
              <Typography variant="body2" color="text.secondary">追蹤號碼</Typography>
              {firstItem.tracking_number || firstItem.payment_no ? (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body1" sx={{ mr: 1 }}>
                    {firstItem.tracking_number || (
                      firstItem.logistics_sub_type === 'FAMIC2C' || 
                      (firstItem.store_id && (firstItem.store_id.startsWith('F') || /^[A-Z]\d{5}$/.test(firstItem.store_id))) 
                        ? firstItem.payment_no 
                        : firstItem.cvs_validation_no 
                          ? `${firstItem.payment_no}${firstItem.cvs_validation_no}` 
                          : firstItem.payment_no
                    )}
                  </Typography>
                  <IconButton 
                    size="small" 
                    onClick={() => copyToClipboard(
                      firstItem.tracking_number || (
                        firstItem.logistics_sub_type === 'FAMIC2C' || 
                        (firstItem.store_id && (firstItem.store_id.startsWith('F') || /^[A-Z]\d{5}$/.test(firstItem.store_id))) 
                          ? firstItem.payment_no 
                          : firstItem.cvs_validation_no 
                            ? `${firstItem.payment_no}${firstItem.cvs_validation_no}` 
                            : firstItem.payment_no
                      )
                    )}
                    title="複製追蹤碼"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  尚未出貨
                </Typography>
              )}
            </Box>
          </Box>
          
          {/* Order Status */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip 
              label={firstItem.is_received ? "已收到" : "未收到"} 
              size="small" 
              color={firstItem.is_received ? "success" : "default"}
              icon={firstItem.is_received ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
              onClick={() => toggleReceivedStatus(firstItem.order_id, firstItem.source, firstItem.is_received)}
              clickable
            />
            <Chip 
              label={firstItem.is_sent ? "已發貨" : "未發貨"} 
              size="small" 
              color={firstItem.is_sent ? "success" : "default"}
              icon={firstItem.is_sent ? <LocalShippingIcon /> : <InventoryIcon />}
              onClick={() => toggleSentStatus(firstItem.order_id, firstItem.source, firstItem.is_sent)}
              clickable
            />
          </Box>
          
          {/* Order Items */}
          {/* Order Items */}
          <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              商品明細 ({orderItems.length} 項)
            </Typography>
            {orderItems.map((item, index) => {
              
              
              // GET TAG FROM METADATA - EXTRACT NUMBER ONLY FROM SKU
              const fullSku = item.display_sku || item.sku || '';
              // Extract just the number part before any parentheses or spaces
              const skuNumber = fullSku.split('(')[0].split(' ')[0].trim();
           
              const metadata = skuNumber ? (productMetadata[skuNumber] as ProductMetadata) : null;
              const tag = metadata?.tag || '';
              const itemKey = skuNumber || item.outlet_item_id || `${index}`;
              const notesCompositeKey = `${orderId}::${itemKey}`;
              const notes = orderItemNotes[notesCompositeKey] || '';
            

              return (
                <Box 
                  key={item.id || `${item.order_id}-${item.outlet_item_id}-${index}`}
                  sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    alignItems: 'center',
                    py: 1,
                    borderBottom: index < orderItems.length - 1 ? 1 : 0,
                    borderColor: 'divider'
                  }}
                >
                  {item.image_url && (
                  <Box
                    sx={{
                      width: 60,
                      height: 60,
                      cursor: 'pointer',
                      position: 'relative',
                      '&:hover': {
                        opacity: 0.8,
                        '& .image-overlay': {
                          opacity: 1
                        }
                      }
                    }}
                    onClick={() => handleOpenImageGallery(item)}
                  >
                    <img 
                      src={item.image_url} 
                      alt={item.title || 'Product'} 
                      style={{ 
                        width: '100%', 
                        height: '100%', 
                        objectFit: 'contain',
                        borderRadius: 4,
                        border: '1px solid #e0e0e0'
                      }} 
                    />
                    {/* Show image count badge if multiple images exist */}
                    {(() => {
                      let imageCount = 1;
                      if (item.all_images) {
                        try {
                          const allImagesData = typeof item.all_images === 'string' 
                            ? JSON.parse(item.all_images) 
                            : item.all_images;
                          if (Array.isArray(allImagesData)) {
                            imageCount = allImagesData.length;
                          }
                        } catch (e) {
                          // Ignore parsing errors
                        }
                      }
                      
                      return imageCount > 1 ? (
                        <Chip
                          size="small"
                          label={`${imageCount}`}
                          sx={{
                            position: 'absolute',
                            bottom: 2,
                            right: 2,
                            height: 18,
                            fontSize: '0.7rem',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            '& .MuiChip-label': {
                              px: 0.5
                            }
                          }}
                        />
                      ) : null;
                    })()}
                    
                    {/* Hover overlay */}
                    <Box
                      className="image-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        borderRadius: 1
                      }}
                    >
                      <VisibilityIcon sx={{ color: 'white', fontSize: 20 }} />
                    </Box>
                  </Box>
                )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1">{item.title || 'Unnamed Product'}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" color="text.secondary">
                        SKU: {item.display_sku || item.sku || 'No SKU'}
                      </Typography>
                      {/* DISPLAY TAG NEXT TO SKU */}
                      {tag && (
                        <Chip
                          label={tag}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {/* 貨架格 — where the physical outlet item is shelved, so the
                          picker can locate it for this order. Hidden once 出貨
                          (is_sent): the item has left the shelf, bin released. */}
                      {item.slot_number != null && !item.is_sent && (
                        <Tooltip title={`此商品放在出清 ${item.slot_number} 號格`} arrow>
                          <Chip
                            label={`📦 ${item.slot_number} 號格`}
                            size="small"
                            sx={{
                              bgcolor: '#eef2ff',
                              color: '#4338ca',
                              fontWeight: 700,
                              border: '1px solid #c7d2fe',
                            }}
                          />
                        </Tooltip>
                      )}

                    </Box>
                    {/* 備註 field */}
                    {editingNotesKey === notesCompositeKey ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <TextField
                          size="small"
                          value={editingNotesValue}
                          onChange={(e) => setEditingNotesValue(e.target.value)}
                          placeholder="輸入備註..."
                          variant="outlined"
                          sx={{ flex: 1, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.85rem' } }}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleSaveNotes(notesCompositeKey);
                            } else if (e.key === 'Escape') {
                              setEditingNotesKey(null);
                            }
                          }}
                          disabled={savingNotes}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleSaveNotes(notesCompositeKey)}
                          disabled={savingNotes}
                          sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                        >
                          儲存
                        </Button>
                        <Button
                          size="small"
                          onClick={() => setEditingNotesKey(null)}
                          disabled={savingNotes}
                          sx={{ minWidth: 'auto', px: 1, py: 0.5, fontSize: '0.75rem' }}
                        >
                          取消
                        </Button>
                      </Box>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          color: notes ? 'text.secondary' : '#bbb',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontStyle: notes ? 'normal' : 'italic',
                          '&:hover': { color: 'primary.main' }
                        }}
                        onClick={() => {
                          setEditingNotesKey(notesCompositeKey);
                          setEditingNotesValue(notes);
                        }}
                      >
                        {notes ? `備註: ${notes}` : '+ 新增備註'}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body1">${Math.round(parseFloat(item.sale_price || '0'))}</Typography>
                    {item.original_price && parseFloat(item.original_price) > parseFloat(item.sale_price || '0') && (
                      <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                        ${Math.round(parseFloat(item.original_price))}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Paper>
      );
    })}
    {showPager && (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
        <Pagination count={pageCount} page={safePage} onChange={(_, v) => setPage(v)} color="primary" />
      </Box>
    )}
  </Box>
  );
})()}



      {/* Shipping Confirmation Dialog */}
      <Dialog
        open={shippingDialogOpen}
        onClose={() => setShippingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>確認出貨</DialogTitle>
        <DialogContent>
          {currentOrder && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                您即將標記以下訂單為已出貨狀態。
              </Alert>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1">
                  <strong>訂單編號:</strong> {currentOrder.order_id || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>商品:</strong> {currentOrder.title || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>客戶:</strong> {currentOrder.customer_phone || currentOrder.customer_email || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>配送方式:</strong>{' '}
                  {
                    getDeliveryMethodInfo(currentOrder.delivery_method ?? '', {
                      logisticsSubType: currentOrder.logistics_sub_type,
                      storeId: currentOrder.store_id
                    }).label
                  }
                </Typography>
                {currentOrder.store_name && currentOrder.delivery_method !== 'shipping' && (
                  <Typography variant="body1">
                    <strong>取貨門市:</strong> {currentOrder.store_name} ({currentOrder.store_id || 'N/A'})
                  </Typography>
                )}
                {currentOrder.delivery_method === 'shipping' && currentOrder.shipping_address && (
                  <Typography variant="body1">
                    <strong>收件地址:</strong>{' '}
                    {(() => {
                      const addr = currentOrder.shipping_address;
                      if (typeof addr === 'string') {
                        try {
                          const parsed = JSON.parse(addr);
                          return [parsed.city, parsed.district, parsed.address1].filter(Boolean).join('');
                        } catch { return addr; }
                      }
                      if (typeof addr === 'object') {
                        const a = addr as any;
                        return [a.city, a.district, a.address1].filter(Boolean).join('');
                      }
                      return String(addr);
                    })()}
                  </Typography>
                )}
                {currentOrder.delivery_method === 'shipping' && currentOrder.sf_waybill_no && (
                  <Typography variant="body1" color="primary">
                    <strong>順豐單號:</strong> {currentOrder.sf_waybill_no}
                  </Typography>
                )}
                {currentOrder.delivery_method === 'shipping' && !currentOrder.sf_waybill_no && currentOrder.hsinchu_tracking_number && (
                  <Typography variant="body1" color="primary">
                    <strong>新竹物流追蹤號碼:</strong> {currentOrder.hsinchu_tracking_number}
                  </Typography>
                )}
                
                {/* Show tracking number if available */}
                {(currentOrder.tracking_number || currentOrder.payment_no) && (
                  <Typography variant="body1" color="primary">
                    <strong>追蹤號碼:</strong>{' '}
                    {currentOrder.tracking_number || (
                      currentOrder.logistics_sub_type === 'FAMIC2C' || 
                      (currentOrder.store_id && (currentOrder.store_id.startsWith('F') || /^[A-Z]\d{5}$/.test(currentOrder.store_id))) 
                        ? currentOrder.payment_no 
                        : currentOrder.cvs_validation_no 
                          ? `${currentOrder.payment_no}${currentOrder.cvs_validation_no}` 
                          : currentOrder.payment_no
                    )}
                  </Typography>
                )}
                
                {/* Show total order amount if there are multiple items */}
                {(() => {
                  // Scope to the SHIPMENT GROUP (合併出貨), not just the primary order — the
                  // amount typed here becomes ECPay's 代收, and a combined parcel carries
                  // every member order's items.
                  const groupIds = shipGroupOrderIds.length
                    ? shipGroupOrderIds
                    : [currentOrder.order_id].filter(Boolean) as string[];
                  const orderItems = salesHistory
                    ? salesHistory.filter(item => item.order_id && groupIds.includes(item.order_id))
                    : [];
                  const calculatedTotal = sumNetAmount(orderItems);          // net of 購物金
                  const grossTotal = sumGrossAmount(orderItems);
                  const creditTotal = grossTotal - calculatedTotal;
                  const { cod: codDue, paid: alreadyPaid } = splitCodAmount(orderItems);
                  const mixedGroup = alreadyPaid > 0 && codDue > 0;

                  if (orderItems.length > 1) {
                    return (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>{codDue > 0 ? '代收金額:' : '訂單總金額:'}</strong>
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={editableAmount}
                            onChange={(e) => setEditableAmount(e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            }}
                            error={mixedGroup && parseInt(editableAmount || '0') > codDue}
                            helperText={mixedGroup
                              ? `貨到付款 $${codDue}（已付款 $${alreadyPaid} 不再收）· 合計 $${calculatedTotal}，共 ${orderItems.length} 項商品`
                              : creditTotal > 0
                                ? `原價 $${grossTotal} − 購物金 $${creditTotal} = 應收 $${calculatedTotal} (共 ${orderItems.length} 項商品)`
                                : `原計算金額: $${calculatedTotal} (共 ${orderItems.length} 項商品)`}
                          />
                        </Box>
                        {mixedGroup && (
                          <Alert severity="warning" sx={{ mb: 2 }}>
                            合併出貨中有 <strong>已付款</strong> 的訂單（${alreadyPaid}）。代收只能收
                            <strong> ${codDue}</strong>，否則客人會被重複收費。
                          </Alert>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          此商品金額: ${lineNetAmount(currentOrder)}
                        </Typography>
                      </>
                    );
                  } else {
                    const net = lineNetAmount(currentOrder);
                    const gross = Math.floor(parseFloat(currentOrder.sale_price || '0'));
                    const credit = gross - net;
                    return (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>金額:</strong>
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={editableAmount}
                          onChange={(e) => setEditableAmount(e.target.value)}
                          InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          }}
                          helperText={credit > 0
                            ? `原價 $${gross} − 購物金 $${credit} = 應收 $${net}`
                            : `原價格: $${net}`}
                        />
                      </Box>
                    );
                  }
                })()}
              </Box>
              
              <Alert severity="warning">
                確認後，系統將自動更新訂單狀態。請確保您已準備好出貨。
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShippingDialogOpen(false)} disabled={processingShipping}>
            取消
          </Button>
          <Button 
            onClick={confirmShipOrder} 
            color="primary" 
            variant="contained"
            disabled={processingShipping}
            startIcon={processingShipping ? <CircularProgress size={20} /> : <LocalShippingIcon />}
          >
            {processingShipping ? '處理中...' : '確認出貨'}
          </Button>
        </DialogActions>
      </Dialog>
      <BatchPrintDialog />
      {/* Image Gallery Dialog */}
<Dialog
  open={imageGalleryOpen}
  onClose={() => setImageGalleryOpen(false)}
  maxWidth="lg"
  fullWidth
  onKeyDown={handleKeyPress}
  PaperProps={{
    sx: {
      backgroundColor: 'black',
      boxShadow: 24,
    }
  }}
>
  <DialogTitle sx={{ 
    backgroundColor: 'rgba(0, 0, 0, 0.9)', 
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  }}>
    <Typography variant="h6">{selectedProductTitle}</Typography>
    <IconButton
      onClick={() => setImageGalleryOpen(false)}
      sx={{ color: 'white' }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>
  
  <DialogContent sx={{ position: 'relative', p: 0, backgroundColor: 'black' }}>
    {/* Navigation buttons */}
    {currentProductImages.length > 1 && (
      <>
        <IconButton
          onClick={handlePreviousImage}
          sx={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 1
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        
        <IconButton
          onClick={handleNextImage}
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'white',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
            },
            zIndex: 1
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </>
    )}
    
    {/* Main image */}
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        maxHeight: '80vh',
        position: 'relative'
      }}
    >
      {currentProductImages[currentImageIndex] && (
        <img
          src={currentProductImages[currentImageIndex]}
          alt={`${selectedProductTitle} - ${currentImageIndex + 1}`}
          style={{
            maxWidth: '100%',
            maxHeight: '80vh',
            objectFit: 'contain'
          }}
        />
      )}
    </Box>
    
    {/* Image counter */}
    {currentProductImages.length > 1 && (
      <Typography
        sx={{
          position: 'absolute',
          bottom: 16,
          left: '50%',
          transform: 'translateX(-50%)',
          color: 'white',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          px: 2,
          py: 0.5,
          borderRadius: 1
        }}
      >
        {currentImageIndex + 1} / {currentProductImages.length}
      </Typography>
    )}
    
    {/* Thumbnail strip */}
    {currentProductImages.length > 1 && (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          p: 2,
          justifyContent: 'center',
          overflowX: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          '&::-webkit-scrollbar': {
            height: 6,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,.3)',
            borderRadius: 3,
          }
        }}
      >
        {currentProductImages.map((img, index) => (
          <Box
            key={index}
            component="img"
            src={img}
            alt={`Thumbnail ${index + 1}`}
            onClick={() => setCurrentImageIndex(index)}
            sx={{
              width: 80,
              height: 80,
              objectFit: 'cover',
              cursor: 'pointer',
              opacity: currentImageIndex === index ? 1 : 0.5,
              border: currentImageIndex === index ? '2px solid white' : '2px solid transparent',
              borderRadius: 1,
              transition: 'all 0.2s',
              '&:hover': {
                opacity: 1
              }
            }}
          />
        ))}
      </Box>
    )}
  </DialogContent>
  
  <DialogActions sx={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}>
    <Typography variant="caption" sx={{ color: 'white', flex: 1, ml: 2 }}>
      提示: 使用 ← → 方向鍵導覽，ESC 關閉
    </Typography>
    <Button onClick={() => setImageGalleryOpen(false)} sx={{ color: 'white' }}>
      關閉
    </Button>
  </DialogActions>
</Dialog>
    {/* Refund Dialog */}
    <Dialog open={refundDialogOpen} onClose={() => !processingRefund && setRefundDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>信用卡退刷</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Typography variant="body2">
            訂單編號: <strong>#{refundOrderId}</strong>
          </Typography>
          {refundTradeNo && (
            <Typography variant="body2">
              綠界交易編號: <strong>{refundTradeNo}</strong>
            </Typography>
          )}
          <Typography variant="body2">
            訂單金額: <strong>${refundMaxAmount}</strong>
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Button
              variant={refundFullAmount ? "contained" : "outlined"}
              size="small"
              onClick={() => {
                setRefundFullAmount(true);
                setRefundAmount(String(refundMaxAmount));
              }}
            >
              全額退刷
            </Button>
            <Button
              variant={!refundFullAmount ? "contained" : "outlined"}
              size="small"
              onClick={() => {
                setRefundFullAmount(false);
                setRefundAmount('');
              }}
            >
              部分退刷
            </Button>
          </Box>

          {!refundFullAmount && (
            <TextField
              label="退刷金額"
              type="number"
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ min: 1, max: refundMaxAmount }}
              helperText={`最大可退刷金額: $${refundMaxAmount}`}
            />
          )}

          <Alert severity="warning" sx={{ mt: 1 }}>
            退刷操作無法撤銷，請確認退刷金額正確。
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRefundDialogOpen(false)} disabled={processingRefund}>
          取消
        </Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleRefund}
          disabled={processingRefund || !refundAmount || parseInt(refundAmount, 10) <= 0}
        >
          {processingRefund ? <CircularProgress size={20} /> : '確認退刷'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Combine Orders Dialog */}
    <Dialog
      open={combineDialogOpen}
      onClose={() => !combineProcessing && setCombineDialogOpen(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        合併出貨
        <IconButton
          onClick={() => setCombineDialogOpen(false)}
          disabled={combineProcessing}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {(() => {
          const anchor = combineCandidates[0];
          const customerName = anchor?.customer_name || anchor?.customer_phone || '客戶';
          const destLabel = anchor
            ? (anchor.delivery_method === 'shipping'
                ? '宅配到府'
                : (anchor.store_name || anchor.store_id || '超商取貨'))
            : '';
          const selectedTotal = combineCandidates
            .filter((c) => combineSelectedIds.includes(c.order_id as string))
            .reduce((sum, c) => sum + (orderTotals[c.order_id as string] || 0), 0);
          return (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                將 <strong>{customerName}</strong> 的多筆未出貨訂單合併為「一個包裹、一張出貨單」。
                送達點：<strong>{destLabel}</strong>。各訂單的營收與退款仍維持獨立，隨時可取消合併。
              </Alert>
              <Typography variant="body2" sx={{ mb: 1 }}>
                勾選要合併的訂單，並選擇主單（出貨單會以主單建立）：
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">合併</TableCell>
                      <TableCell padding="checkbox">主單</TableCell>
                      <TableCell>訂單編號</TableCell>
                      <TableCell>商品</TableCell>
                      <TableCell align="right">金額</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {combineCandidates.map((c) => {
                      const cid = c.order_id as string;
                      const items = (salesHistory || []).filter((s) => s.order_id === cid);
                      const checked = combineSelectedIds.includes(cid);
                      return (
                        <TableRow key={cid} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={checked}
                              onChange={() => toggleCombineSelection(cid)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell padding="checkbox">
                            <Radio
                              checked={combinePrimaryId === cid && checked}
                              onChange={() => setCombinePrimaryId(cid)}
                              disabled={!checked}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            #{cid}
                            {c.ecpay_logistics_id && (
                              <Chip
                                label="已建立物流"
                                size="small"
                                color="success"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            {items.slice(0, 3).map((it, i) => (
                              <Typography
                                key={i}
                                variant="caption"
                                display="block"
                                noWrap
                                sx={{ maxWidth: 260 }}
                              >
                                {it.title || it.sku}
                                {it.variant_details ? ` (${it.variant_details})` : ''}
                              </Typography>
                            ))}
                            {items.length > 3 && (
                              <Typography variant="caption" color="text.secondary">
                                +{items.length - 3} 項
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">${orderTotals[cid] || 0}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  已選 {combineSelectedIds.length} 筆
                </Typography>
                <Typography variant="subtitle1">
                  合併後總金額：<strong>${selectedTotal}</strong>
                </Typography>
              </Box>
              {anchor && anchor.delivery_method !== 'shipping' && selectedTotal >= 699 ? (
                <Alert severity="success" sx={{ mt: 1 }}>
                  合併後小計 ${selectedTotal} ≥ NT$699，將自動免運費；已付款訂單的運費會退回購物金。
                </Alert>
              ) : combineSelectedIds.length >= 2 ? (
                <Alert severity="info" sx={{ mt: 1 }}>
                  合併為單一包裹，運費僅收取一次（多收的運費會退回購物金）。
                </Alert>
              ) : null}
            </>
          );
        })()}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCombineDialogOpen(false)} disabled={combineProcessing}>
          取消
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={combineProcessing ? <CircularProgress size={18} /> : <CallMergeIcon />}
          onClick={confirmCombine}
          disabled={combineProcessing || combineSelectedIds.length < 2}
        >
          {combineProcessing ? '合併中...' : `確認合併 (${combineSelectedIds.length})`}
        </Button>
      </DialogActions>
    </Dialog>

    {/* 國際宅配 — SF International (順豐) shipping modal */}
    {sfModalOrder && (
      <SFInternationalShippingModal
        open={!!sfModalOrder}
        onClose={() => setSfModalOrder(null)}
        order={sfModalOrder}
        country={sfCountryOf(sfModalOrder) || 'HK'}
        enqueueSnackbar={enqueueSnackbar}
        onShippingPrepared={handleSFShippingPrepared}
      />
    )}

    </Paper>

  );
}

export default SalesHistoryTab;